import { mulDivRound, normalizePlu, parseSerials, stockStatus, type ShelfLifeUnit } from '@ims/core'
import type { BatchLevel, JsonValue, ProductCodeRow, ProductRow, RowOf } from '@ims/data'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  cx,
  EmptyState,
  Field,
  IconButton,
  IconTile,
  Input,
  Modal,
  PageHeader,
  Pagination,
  RowTitle,
  SearchInput,
  Select,
  SkeletonDetail,
  SkeletonTable,
  StateSwap,
  Switch,
  Table,
  TD,
  Textarea,
  TH,
  TR,
  useToast,
  FilterPill,
  FilterPills,
  Kbd,
  ListFrame,
  ListToolbar,
  RowActions,
  Segmented,
} from '@ims/ui'
import {
  ArrowLeft,
  ArrowsSplit,
  BookOpenText,
  CheckCircle2,
  CookingPot,
  Download,
  GripVertical,
  Hand,
  ListChecks,
  Package,
  Pencil,
  Plus,
  SlidersHorizontal,
  Trash2,
  Truck,
  Upload,
  Wrench,
} from '@ims/ui/icons'
import { useEffect, useEffectEvent, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { LocationSelect, MoneyInput, PhoneAdd, QtyInput, useLookups } from '../components.tsx'
import { ScanButton, ScanSerialsButton, type ScanResult } from '../scanner.tsx'
import {
  errorMessage,
  useFeature,
  useFieldGroups,
  useLive,
  useSettings,
  useUrlAction,
} from '../hooks.ts'
import { RequestMoreDialog } from './purchase-requests.tsx'
import { attributeFields, cleanAttributes, parseTags } from '../lib/catalog.ts'
import { moveClick, useOpen } from '../lib/motion.ts'
import { useListKeys } from '../lib/list-keys.ts'
import { downloadBlob, todayStamp } from '../lib/download.ts'
import { expiryBadge, followExpiry, noBatchLevels } from '../lib/batches.ts'
import { clearDraft, draftKey, readDraft, saveDraft } from '../lib/drafts.ts'
import { mergeFormDraft, parseFormDraft } from '../lib/form-draft.ts'
import { DraftBanner } from './draft-banner.tsx'
import { money, qty, todayIso } from '../lib/format.ts'
import { productsToRows } from '../lib/import/index.ts'
import { createApi, suggestedName, type BarcodeSuggestion } from '../lib/api.ts'
import { useSession } from '../session.tsx'
import { BulkEditDialog } from './bulk-edit.tsx'
import {
  batchOp,
  deleteOp,
  insertOp,
  newCategory,
  newBrand,
  newMovement,
  newProduct,
  newProductCode,
  newUnit,
  serialOp,
  updateOp,
} from '../lib/rows.ts'
import { nextSortOrder } from '../lib/categories.ts'
import { MAX_VARIANTS, variantChanges, variantName } from '../lib/variants.ts'
import { SerialsCard } from './serials.tsx'
import {
  VariantsCard,
  VariantsEditor,
  draftOptions,
  optionDrafts,
  variantDrafts,
  type VariantEdit,
} from './variants.tsx'
import { findUnit, presetFor } from '../lib/units.ts'
import {
  AttributeFields,
  BrandField,
  PacksEditor,
  ProductDetailsCard,
  SectionTitle,
  packDrafts,
  type PackDraft,
} from './product-extras.tsx'
import { PhotoField, ProductPhoto, usePhotoUrls } from '../photos.tsx'
import { SupplierPicker } from '../party-picker.tsx'
import { RecipeCard } from './recipe.tsx'
import { CategoriesTab, categoriesSubtitle } from './categories.tsx'
import {
  FIELD_HEADING,
  FieldsButton,
  columnFields,
  fieldAlign,
  fieldCell,
  fieldText,
  phoneLine,
  useFieldData,
  useListFieldsHere,
  nextExpiries,
  type FieldContext,
} from '../list-fields.tsx'
import { StatCard, StockHistory } from './stock-history.tsx'
import { daysAgo, soldSince } from '../lib/product-stats.ts'
import { ReceiveStockDialog } from './receive.tsx'
import {
  EditBatchDialog,
  ExpiryHint,
  SplitBatchesDialog,
  canSplit,
  type SplitSource,
} from './batch-split.tsx'
import { MenuList, type MenuCounts, type MenuShow } from './menu-list.tsx'
import { IngredientsList, type IngredientCounts, type IngredientShow } from './ingredients-list.tsx'
import { nextSku, skuFromName } from '../lib/sku.ts'
import { useCan, useRepo, useWorkspace } from '../workspace.tsx'
import { finePointer } from '../lib/pointer.ts'
import { useUnsavedWarning } from '../lib/unsaved.ts'
import { SectionTabs } from '../layout/sections.tsx'

const PAGE = 25

export function StockBadge({
  qtyMilli,
  minMilli,
  track,
  service,
  count,
}: {
  qtyMilli: number
  minMilli: number
  track: boolean
  /** A workshop's untracked product is labour or a fixed job. */
  service?: boolean
  /** The stock in words ("4 pcs"), shown in a Low or Below zero pill when the list hides it. */
  count?: string | null
}) {
  if (!track) return <Badge>{service ? 'Service' : 'Not tracked'}</Badge>
  // Sold more than was recorded: usually a delivery not entered yet, or a count to fix.
  if (qtyMilli < 0) return <Badge tone="danger">Below zero{count ? ` · ${count}` : ''}</Badge>
  const status = stockStatus(qtyMilli, minMilli)
  // A sale or a delivery that moves it across a line: the new badge settles in.
  return (
    <StateSwap state={status}>
      {status === 'out_of_stock' ? (
        <Badge tone="danger">Out of stock</Badge>
      ) : status === 'low_stock' ? (
        <Badge tone="warning">Low{count ? ` · ${count}` : ''}</Badge>
      ) : (
        <Badge tone="success">In stock</Badge>
      )}
    </StateSwap>
  )
}

/** Stock per product across all locations. */
function useStockTotals() {
  return (
    useLive(
      async (repo) => {
        const totals = new Map<string, number>()
        for (const l of await repo.stock.levels())
          totals.set(l.product_id, (totals.get(l.product_id) ?? 0) + l.qty_milli)
        return totals
      },
      [],
      ['stock_movements'],
    ).data ?? new Map<string, number>()
  )
}

/** Each variant group's variants. */
function useVariantsByGroup() {
  return (
    useLive(
      async (repo) => {
        const out = new Map<string, ProductRow[]>()
        const rows = (await repo.list('products', { where: { kind: 'variant' }, limit: 1_000_000 }))
          .rows
        for (const v of rows) {
          if (!v.parent_id) continue
          const list = out.get(v.parent_id) ?? []
          list.push(v)
          out.set(v.parent_id, list)
        }
        return out
      },
      [],
      ['products'],
    ).data ?? new Map<string, ProductRow[]>()
  )
}

/** Every product in the import template layout, as an .xlsx download. */
function useExportProducts() {
  const repo = useRepo()
  const toast = useToast()
  const lookups = useLookups()
  const { platform } = useSession()
  const fieldGroups = useFieldGroups()
  const shownAttributes = attributeFields(fieldGroups.groups, fieldGroups.custom).flatMap(
    (s) => s.fields,
  )
  const [busy, setBusy] = useState(false)
  const run = async () => {
    setBusy(true)
    // One toast from start to result: a big catalogue takes a few seconds.
    const working = toast.loading('Preparing the export…')
    try {
      const [products, levels] = await Promise.all([
        repo.list('products', { orderBy: { column: 'name' }, limit: 1_000_000 }),
        repo.stock.levels(),
      ])
      const stock = new Map<string, number>()
      for (const l of levels) stock.set(l.product_id, (stock.get(l.product_id) ?? 0) + l.qty_milli)
      const rows = productsToRows(products.rows, lookups, stock, shownAttributes)
      const blob = await platform.spreadsheets.write([{ name: 'Products', rows }])
      downloadBlob(blob, `products-${todayStamp()}.xlsx`)
      const n = products.rows.length
      working.success('Export downloaded', { detail: `${n} product${n === 1 ? '' : 's'}` })
    } catch (error) {
      working.error('The export failed', { detail: errorMessage(error) })
    } finally {
      setBusy(false)
    }
  }
  return { busy, run }
}

/** The shop list's row the arrow keys are on. */
export const SHOP_ROW_ACTIVE = 'sm:bg-stock-wash sm:shadow-[inset_3px_0_0_var(--color-stock)]'

export function ProductsScreen({ view }: { view?: 'menu' | 'ingredients' } = {}) {
  const settings = useSettings()
  const lookups = useLookups()
  const navigate = useNavigate()
  const openItem = useOpen()
  const canEdit = useCan('staff')
  const canBulk = useCan('manager')
  const repo = useRepo()
  const exporter = useExportProducts()
  // Bulk edit (managers): ticked ids survive paging and searching.
  const [selecting, setSelecting] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [bulkOpen, setBulkOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  // 'service': a new product that is labour or a fixed job (workshops).
  const [editingState, setEditing] = useState<ProductRow | 'new' | 'service' | null>(null)
  const workshop = settings?.business_type === 'workshop'
  const [askedNew, clearAskedNew] = useUrlAction('new')
  // Clearing `?new=1` is a navigation that lands a moment later: once the form it opened
  // has closed, the address no longer opens one (a form left open in that moment kept the
  // last product's answers, and "Add another" saved its new category twice).
  const [askedDone, setAskedDone] = useState(false)
  if (askedDone && !askedNew) setAskedDone(false)
  const editing = editingState ?? (askedNew && !askedDone && canEdit ? 'new' : null)
  // Each form opened gets a fresh one, never the last one's state.
  const [formRun, setFormRun] = useState(0)
  // A code the camera read that no product has: the new product's barcode.
  const [scanned, setScanned] = useState<string | null>(null)
  // A product just added: a box offers another one or its page, as quick bill's "Sale
  // complete" does. "Add another" starts the next with its category, unit and VAT.
  const [added, setAdded] = useState<{ product: ProductRow; kind: 'new' | 'service' } | null>(null)
  const [like, setLike] = useState<ProductRow | null>(null)
  // A restaurant's Menu has its own list (screens/menu-list.tsx).
  const menuView = view === 'menu'
  const [menuShow, setMenuShow] = useState<MenuShow>('menu')
  const [menuCounts, setMenuCounts] = useState<MenuCounts | null>(null)
  // And Ingredients (screens/ingredients-list.tsx).
  const ingredientsView = view === 'ingredients'
  const [ingredientShow, setIngredientShow] = useState<IngredientShow>('all')
  const [ingredientCounts, setIngredientCounts] = useState<IngredientCounts | null>(null)
  const closeForm = () => {
    setEditing(null)
    setScanned(null)
    if (askedNew) {
      setAskedDone(true)
      clearAskedNew()
    }
  }
  // Restaurant mode keeps what is sold (the menu) apart from what the kitchen uses.
  const forSale = view === undefined ? undefined : view === 'menu'
  const words =
    view === 'ingredients'
      ? {
          title: 'Ingredients',
          subtitle: 'What the kitchen uses: counted, received and used in recipes, never sold.',
          add: 'Add ingredient',
          column: 'Ingredient',
          search: 'Search name or code…',
          many: 'ingredients',
          icon: CookingPot,
          none: 'No ingredients yet',
          hint: 'Add flour, oil, meat and whatever else your dishes use. A dish’s recipe then takes them out of stock.',
        }
      : view === 'menu'
        ? {
            title: 'Menu',
            subtitle: 'What your guests can order, with prices and VAT.',
            add: 'Add item',
            column: 'Item',
            search: 'Search name or code…',
            many: 'items',
            icon: BookOpenText,
            none: 'The menu is empty',
            hint: 'Add your dishes and drinks, or bring in your whole list from Excel in one go.',
          }
        : {
            title: 'Products',
            subtitle: 'Your catalogue, prices and VAT.',
            add: 'Add product',
            column: 'Product',
            search: 'Name, SKU, barcode or category…',
            many: 'products',
            icon: Package,
            none: 'No products yet',
            hint: 'Add your first product, or bring in your whole list from Excel in one go.',
          }
  // A shop's list (docs/plans/lists-orders-style.md): a stock switch and category pills,
  // worked out in the query, 25 a page.
  const [shopShow, setShopShowValue] = useState<'all' | 'low' | 'out' | 'inactive'>('all')
  const setShopShow = (v: typeof shopShow) => {
    setShopShowValue(v)
    setOffset(0)
  }
  // The tab and the category filter live in the address (?tab=categories,
  // ?category=<id> or none), so a Categories row and Ctrl K open the list already filtered.
  const [params, setParams] = useSearchParams()
  const listTab =
    (view === undefined || view === 'menu') && params.get('tab') === 'categories'
      ? 'categories'
      : 'products'
  const shopTab = view === undefined ? listTab : 'products'
  // A restaurant's categories are its menu's sections, in their own order (menu-sections.md).
  const menuCategories = view === 'menu' && listTab === 'categories'
  const [ordering, setOrdering] = useState(false)
  const shopCategory = view === undefined ? params.get('category') : null
  const setParam = (key: string, value: string | null) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        if (value) next.set(key, value)
        else next.delete(key)
        return next
      },
      { replace: true },
    )
  const setShopCategory = (v: string | null) => setParam('category', v)
  // A new category (from a pill, a Categories row or Ctrl K) starts on the first page.
  const [pagedCategory, setPagedCategory] = useState(shopCategory)
  if (pagedCategory !== shopCategory) {
    setPagedCategory(shopCategory)
    setOffset(0)
  }
  const [addingCategory, setAddingCategory] = useState(false)
  const listFilter = {
    scope: 'catalog' as const,
    forSale,
    categoryId: shopCategory ?? undefined,
    stock: shopShow === 'all' ? undefined : shopShow,
  }
  const page = useLive(
    (repo) => repo.products.search(search, { ...listFilter, limit: PAGE, offset }),
    [search, offset, forSale, shopCategory, shopShow],
    ['products', 'stock_movements'],
  )
  const summary = useLive(
    async (repo) => (repo.products.summary ? repo.products.summary({ forSale }) : null),
    [forSale],
    ['products', 'stock_movements'],
  ).data
  const [receiving, setReceiving] = useState<ProductRow | null>(null)
  const [activeRow, setActiveRow] = useState<string | null>(null)
  // The category pills: every category with a product, by name.
  const categoryPills = (summary?.categories ?? [])
    .flatMap((c) => {
      const found = c.categoryId ? lookups.categories.find((l) => l.id === c.categoryId) : null
      return found && c.count > 0 ? [{ id: found.id, name: found.name, count: c.count }] : []
    })
    .sort((a, b) => a.name.localeCompare(b.name))
  // Products without a category: a last pill once some have one.
  const uncategorized = summary?.categories.find((c) => c.categoryId === null)?.count ?? 0
  if (categoryPills.length > 0 && (uncategorized > 0 || shopCategory === 'none'))
    categoryPills.push({ id: 'none', name: 'No category', count: uncategorized })
  const categoryLine = categoriesSubtitle(summary, lookups.categories.length)
  const stock = useStockTotals()
  const variantsOf = useVariantsByGroup()
  const pageIds = page.data?.rows.map((p) => p.id) ?? []
  // A row shows its photo when it has one (signed in one request; nothing offline).
  const listPhotos = usePhotoUrls(page.data?.rows.map((p) => p.image_path) ?? [])
  const restaurant = settings?.restaurant && settings.business_type === 'restaurant'
  const pageAllSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id))
  const toggle = (ids: string[], on: boolean) =>
    setSelected((current) => {
      const next = new Set(current)
      for (const id of ids) {
        if (on) next.add(id)
        else next.delete(id)
      }
      return next
    })
  const selectAllMatching = async () => {
    const all = await repo.products.search(search, { ...listFilter, limit: 1_000_000 })
    toggle(
      all.rows.map((p) => p.id),
      true,
    )
  }
  // The shop list's keys (the Menu and Ingredients lists have their own).
  const shopRows = page.data?.rows ?? []
  const activeProduct = shopRows.find((p) => p.id === activeRow)
  const receivable = (p: ProductRow) =>
    canEdit && p.kind !== 'group' && p.track_stock && (stock.get(p.id) ?? 0) <= p.min_stock_milli
  const shopList = useRef<HTMLDivElement>(null)
  // The fields the shop chose to show (lib/list-fields.ts); Name, Price and Status always.
  const shopFields = useListFieldsHere('products')
  const shopColumns = columnFields(shopFields.fields)
  const fieldData = useFieldData(shopRows, shopFields.fields)
  const fieldCtx: FieldContext = { lookups, settings, ...fieldData }
  useListKeys({
    enabled: view === undefined && shopTab === 'products',
    ids: shopRows.map((p) => p.id),
    active: activeRow,
    setActive: setActiveRow,
    page: { offset, limit: PAGE, total: page.data?.total ?? 0, setOffset },
    list: shopList,
    open: () => activeProduct && openItem(`/products/${activeProduct.id}`),
    keys: {
      e: () => canEdit && activeProduct && setEditing(activeProduct),
      r: () => activeProduct && receivable(activeProduct) && setReceiving(activeProduct),
    },
  })
  const stopSelecting = () => {
    setSelecting(false)
    setSelected(new Set())
  }
  /** A code from the camera: opens its product, or offers to add it with the code filled in. */
  const onScan = async (code: string): Promise<ScanResult> => {
    const found = await repo.products.lookupCode(code)
    if (found) {
      navigate(`/products/${found.product.id}`)
      return { ok: true, message: `Opened ${found.product.name}` }
    }
    return {
      ok: false,
      message: `No product with the code ${code}`,
      unknown: code,
      action: canEdit
        ? {
            label: words.add,
            run: () => {
              setScanned(code)
              setEditing('new')
            },
          }
        : undefined,
    }
  }

  // Bulk edit's bar under a list that is not a table (Menu, Ingredients).
  const bulkBar = selecting && selected.size > 0 && (
    <div className="sticky bottom-24 mt-3 flex flex-wrap items-center justify-between gap-3 rounded-card border border-border bg-surface px-4 py-3 shadow-card lg:bottom-0">
      <span className="text-sm font-medium">{selected.size} selected</span>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" onClick={() => setSelected(new Set())}>
          Clear
        </Button>
        <Button size="sm" onClick={() => setBulkOpen(true)}>
          Change…
        </Button>
      </div>
    </div>
  )

  return (
    <div>
      <PageHeader
        title={view === 'ingredients' ? 'Menu' : words.title}
        subtitle={
          menuCategories ? (
            <>
              <b className="text-ink">
                {lookups.categories.length}{' '}
                {lookups.categories.length === 1 ? 'category' : 'categories'}
              </b>
              {' · in the order guests and waiters see them'}
            </>
          ) : menuView && menuCounts ? (
            <>
              <b className="text-ink">
                {menuCounts.items} {menuCounts.items === 1 ? 'item' : 'items'}
              </b>
              {` · ${menuCounts.categories} ${menuCounts.categories === 1 ? 'category' : 'categories'} · ${menuCounts.hidden} hidden`}
              {menuCounts.soldOut > 0 && (
                <>
                  {' · '}
                  <button
                    type="button"
                    className="font-semibold text-warning hover:underline"
                    onClick={() => setMenuShow('soldout')}
                  >
                    {menuCounts.soldOut} sold out today
                  </button>
                </>
              )}
              {menuCounts.noRecipe > 0 && (
                <>
                  {' · '}
                  <button
                    type="button"
                    className="font-semibold text-warning hover:underline"
                    onClick={() => setMenuShow('recipe')}
                  >
                    {menuCounts.noRecipe} without a recipe
                  </button>
                </>
              )}
            </>
          ) : ingredientsView && ingredientCounts ? (
            <>
              <b className="text-ink">
                {ingredientCounts.items}{' '}
                {ingredientCounts.items === 1 ? 'ingredient' : 'ingredients'}
              </b>
              {` · ${ingredientCounts.categories} ${ingredientCounts.categories === 1 ? 'category' : 'categories'}`}
              {ingredientCounts.low > 0 && (
                <>
                  {' · '}
                  <button
                    type="button"
                    className="font-semibold text-warning hover:underline"
                    onClick={() => setIngredientShow('low')}
                  >
                    {ingredientCounts.low} low
                  </button>
                </>
              )}
              {ingredientCounts.out > 0 && (
                <>
                  {' · '}
                  <button
                    type="button"
                    className="font-semibold text-danger hover:underline"
                    onClick={() => setIngredientShow('out')}
                  >
                    {ingredientCounts.out} out of stock
                  </button>
                </>
              )}
            </>
          ) : shopTab === 'categories' ? (
            <>
              <b className="text-ink">
                {lookups.categories.length}{' '}
                {lookups.categories.length === 1 ? 'category' : 'categories'}
              </b>
              {categoryLine.without > 0 && (
                <span className="font-semibold text-warning">
                  {' · '}
                  {categoryLine.without} {categoryLine.without === 1 ? 'product' : 'products'}{' '}
                  without one
                </span>
              )}
              {categoryLine.empty > 0 && ` · ${categoryLine.empty} empty`}
            </>
          ) : view === undefined && summary && summary.total > 0 ? (
            <>
              <b className="text-ink">
                {summary.total.toLocaleString('en-IN')}{' '}
                {summary.total === 1 ? 'product' : 'products'}
              </b>
              {summary.low > 0 && (
                <>
                  {' · '}
                  <button
                    type="button"
                    className="font-semibold text-warning hover:underline"
                    onClick={() => setShopShow('low')}
                  >
                    {summary.low} low
                  </button>
                </>
              )}
              {summary.out > 0 && (
                <>
                  {' · '}
                  <button
                    type="button"
                    className="font-semibold text-danger hover:underline"
                    onClick={() => setShopShow('out')}
                  >
                    {summary.out} out of stock
                  </button>
                </>
              )}
              {summary.belowZero > 0 && (
                <span className="font-semibold text-danger"> · {summary.belowZero} below zero</span>
              )}
            </>
          ) : (
            words.subtitle
          )
        }
        secondary={
          shopTab === 'products' &&
          !menuCategories && (
            <>
              {canBulk && (
                <Button
                  variant="secondary"
                  icon={<ListChecks className="size-4" />}
                  onClick={() => (selecting ? stopSelecting() : setSelecting(true))}
                >
                  {selecting ? 'Done selecting' : 'Select'}
                </Button>
              )}
              <Button
                variant="secondary"
                icon={<Download className="size-4" />}
                loading={exporter.busy}
                onClick={() => void exporter.run()}
              >
                Export
              </Button>
              {canEdit && (
                <Button
                  variant="secondary"
                  icon={<Upload className="size-4" />}
                  onClick={() => navigate('/products/import')}
                >
                  Import
                </Button>
              )}
            </>
          )
        }
        actions={
          canEdit &&
          (shopTab === 'categories' || menuCategories ? (
            <>
              {menuCategories && lookups.categories.length > 1 && (
                <Button
                  variant="secondary"
                  icon={<GripVertical className="size-4" />}
                  disabled={ordering}
                  onClick={() => setOrdering(true)}
                >
                  Change order
                </Button>
              )}
              <Button icon={<Plus className="size-4" />} onClick={() => setAddingCategory(true)}>
                New category
              </Button>
            </>
          ) : (
            <>
              {workshop && (
                <Button
                  variant="secondary"
                  icon={<Wrench className="size-4" />}
                  onClick={() => setEditing('service')}
                >
                  Add service
                </Button>
              )}
              <Button
                icon={<Plus className="size-4" />}
                onClick={() => setEditing('new')}
                // The shop's list has its Add by the search below 1024px (PhoneAdd).
                className={cx(view === undefined && 'max-lg:hidden')}
              >
                {words.add}
              </Button>
            </>
          ))
        }
      />
      <SectionTabs section="menu" />
      {view === undefined && (
        <div
          role="tablist"
          aria-label="Products or categories"
          className="mb-4 flex gap-1 overflow-x-auto border-b border-border"
        >
          {[
            { id: 'products', label: 'Products', count: summary?.total },
            { id: 'categories', label: 'Categories', count: lookups.categories.length },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={shopTab === t.id}
              onClick={() => setParam('tab', t.id === 'categories' ? 'categories' : null)}
              className={cx(
                '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm whitespace-nowrap',
                shopTab === t.id
                  ? 'border-stock font-bold text-ink'
                  : 'border-transparent font-semibold text-muted hover:text-ink',
              )}
            >
              {t.label}
              {t.count !== undefined && (
                <span
                  className={cx(
                    'tabular rounded-full px-1.5 text-xs',
                    shopTab === t.id ? 'bg-stock-light text-stock' : 'bg-soft text-muted',
                  )}
                >
                  {t.count.toLocaleString('en-IN')}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {menuCategories ? (
        <CategoriesTab
          menu
          summary={null}
          adding={addingCategory}
          onAddingDone={() => setAddingCategory(false)}
          ordering={ordering}
          onOrderingDone={() => setOrdering(false)}
        />
      ) : ingredientsView ? (
        <>
          <IngredientsList
            show={ingredientShow}
            onShow={setIngredientShow}
            search={search}
            onSearch={setSearch}
            searchEnd={
              <ScanButton
                mode="single"
                onCode={onScan}
                label="Scan to find a product"
                className="size-8"
              />
            }
            canEdit={canEdit}
            selecting={selecting}
            selected={selected}
            onToggle={toggle}
            onEdit={(p) => setEditing(p)}
            onAdd={() => setEditing('new')}
            onCounts={setIngredientCounts}
          />
          {bulkBar}
        </>
      ) : menuView ? (
        <>
          <MenuList
            initialCategory={params.get('category')}
            show={menuShow}
            onShow={setMenuShow}
            search={search}
            onSearch={setSearch}
            searchEnd={
              <ScanButton
                mode="single"
                onCode={onScan}
                label="Scan to find a product"
                className="size-8"
              />
            }
            canEdit={canEdit}
            selecting={selecting}
            selected={selected}
            onToggle={toggle}
            onEdit={(p) => setEditing(p)}
            onAdd={() => setEditing('new')}
            onCounts={setMenuCounts}
          />
          {bulkBar}
        </>
      ) : shopTab === 'categories' ? (
        <CategoriesTab
          summary={summary}
          adding={addingCategory}
          onAddingDone={() => setAddingCategory(false)}
        />
      ) : (
        <>
          <ListToolbar>
            <Segmented
              label="Which products"
              value={shopShow}
              onChange={setShopShow}
              options={[
                { value: 'all', label: 'All' },
                // Stock is summed on the device: not in online-only mode.
                ...(summary
                  ? [
                      { value: 'low' as const, label: 'Low' },
                      { value: 'out' as const, label: 'Out of stock' },
                    ]
                  : []),
                { value: 'inactive', label: 'Inactive' },
              ]}
              className="max-sm:w-full"
            />
            <div className="flex gap-2 max-sm:w-full sm:ml-auto">
              <SearchInput
                listKeys
                className="min-w-0 max-sm:flex-1 sm:w-80"
                value={search}
                onChange={(v) => {
                  setSearch(v)
                  setOffset(0)
                }}
                placeholder={words.search}
                end={
                  <ScanButton
                    mode="single"
                    onCode={onScan}
                    label="Scan to find a product"
                    className="size-8"
                  />
                }
              />
              <FieldsButton list="products" api={shopFields} />
              {canEdit && <PhoneAdd label={words.add} onClick={() => setEditing('new')} />}
            </div>
          </ListToolbar>
          {summary && categoryPills.length > 0 && (
            <FilterPills label="Category" className="mb-3">
              <FilterPill selected={shopCategory === null} onSelect={() => setShopCategory(null)}>
                All {summary.total}
              </FilterPill>
              {categoryPills.map((c) => (
                <FilterPill
                  key={c.id}
                  selected={shopCategory === c.id}
                  onSelect={() => setShopCategory(c.id)}
                >
                  {c.name} {c.count}
                </FilterPill>
              ))}
            </FilterPills>
          )}
          <ListFrame ref={shopList}>
            {selecting && page.data && pageAllSelected && selected.size < page.data.total && (
              <div className="flex flex-wrap items-center gap-2 border-b border-border bg-primary-light/50 px-4 py-2 text-sm">
                All {pageIds.length} on this page are selected.
                <button
                  type="button"
                  className="font-medium text-primary hover:underline"
                  onClick={() => void selectAllMatching()}
                >
                  Select all {page.data.total}{' '}
                  {search || shopShow !== 'all' || shopCategory ? 'matching' : words.many}
                </button>
              </div>
            )}
            {!page.data ? (
              <SkeletonTable cols={4 + shopColumns.length} />
            ) : page.data.rows.length === 0 ? (
              <EmptyState
                icon={<words.icon className="size-6" />}
                title={
                  search || shopCategory
                    ? `No matching ${words.many}`
                    : shopShow === 'low'
                      ? 'Nothing is low'
                      : shopShow === 'out'
                        ? 'Nothing is out of stock'
                        : shopShow === 'inactive'
                          ? `No inactive ${words.many}`
                          : words.none
                }
                description={search || shopShow !== 'all' || shopCategory ? undefined : words.hint}
                action={
                  canEdit &&
                  !search &&
                  shopShow === 'all' &&
                  !shopCategory && (
                    <div className="flex flex-wrap justify-center gap-2">
                      <Button onClick={() => setEditing('new')}>{words.add}</Button>
                      <Button
                        variant="secondary"
                        icon={<Upload className="size-4" />}
                        onClick={() => navigate('/products/import')}
                      >
                        Import from Excel
                      </Button>
                    </div>
                  )
                }
              />
            ) : (
              <>
                <Table stack="cards">
                  <thead>
                    <tr>
                      {selecting && (
                        <TH className="w-10">
                          <input
                            type="checkbox"
                            aria-label="Select this page"
                            className="size-4 accent-primary"
                            checked={pageAllSelected}
                            onChange={(e) => toggle(pageIds, e.target.checked)}
                          />
                        </TH>
                      )}
                      <TH>{words.column}</TH>
                      {shopColumns.map((f) => (
                        <TH key={f} align={fieldAlign(f)}>
                          {FIELD_HEADING[f]}
                        </TH>
                      ))}
                      <TH align="right">Price</TH>
                      <TH>Status</TH>
                      <TH />
                    </tr>
                  </thead>
                  <tbody>
                    {page.data.rows.map((p) => {
                      const variants = p.kind === 'group' ? (variantsOf.get(p.id) ?? []) : null
                      const tracked = variants ? variants.some((v) => v.track_stock) : p.track_stock
                      const inStock = variants
                        ? variants.reduce((a, v) => a + (stock.get(v.id) ?? 0), 0)
                        : (stock.get(p.id) ?? 0)
                      const prices = variants?.map((v) => v.sell_price_paisa) ?? []
                      const low = prices.length ? Math.min(...prices) : p.sell_price_paisa
                      const high = prices.length ? Math.max(...prices) : p.sell_price_paisa
                      const fieldRow = { p, inStock, tracked }
                      return (
                        <TR
                          key={p.id}
                          onClick={() => openItem(`/products/${p.id}`)}
                          aria-current={activeRow === p.id ? 'true' : undefined}
                          className={cx(activeRow === p.id && SHOP_ROW_ACTIVE)}
                        >
                          {selecting && (
                            <TD className="stack-check w-10" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                aria-label={`Select ${p.name}`}
                                className="size-4 accent-primary"
                                checked={selected.has(p.id)}
                                onChange={(e) => toggle([p.id], e.target.checked)}
                              />
                            </TD>
                          )}
                          <TD className="stack-title">
                            <RowTitle
                              tile={
                                p.image_path && listPhotos[p.image_path] ? (
                                  <ProductPhoto
                                    url={listPhotos[p.image_path]}
                                    alt=""
                                    className="size-7 shrink-0 rounded-lg object-cover"
                                  />
                                ) : (
                                  <IconTile area="stock" size="sm">
                                    <Package className="size-4" />
                                  </IconTile>
                                )
                              }
                              title={
                                <Link
                                  to={`/products/${p.id}`}
                                  className="hover:underline focus-visible:underline"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    moveClick(`/products/${p.id}`, 'open')(e)
                                  }}
                                >
                                  {p.name}
                                </Link>
                              }
                              sub={
                                <>
                                  {/* A phone card: the first two chosen fields (by default the category and what is left). */}
                                  <span className="sm:hidden">
                                    {[
                                      ...phoneLine(
                                        'products',
                                        shopFields.fields,
                                        fieldRow,
                                        fieldCtx,
                                      ),
                                      !p.is_active && 'Inactive',
                                    ]
                                      .filter(Boolean)
                                      .join(' · ') || p.sku}
                                  </span>
                                  <span className="max-sm:hidden">
                                    {[
                                      shopFields.has('sku') && p.sku,
                                      variants && `${variants.length} variants`,
                                      !shopFields.has('brand') && lookups.brand(p.brand_id)?.name,
                                      !p.is_active && 'Inactive',
                                    ]
                                      .filter(Boolean)
                                      .join(' · ')}
                                  </span>
                                </>
                              }
                            />
                          </TD>
                          {shopColumns.map((f) => (
                            <TD
                              key={f}
                              align={fieldAlign(f)}
                              className={cx(
                                'text-[13.5px] max-sm:hidden',
                                fieldAlign(f) === 'right' && 'tabular whitespace-nowrap',
                              )}
                            >
                              {fieldCell(f, fieldRow, fieldCtx)}
                            </TD>
                          ))}
                          <TD
                            align="right"
                            className="stack-end stack-r1 whitespace-nowrap font-bold"
                          >
                            {money(low, settings)}
                            {high !== low && `–${money(high, settings)}`}
                          </TD>
                          <TD className="stack-end stack-r2">
                            <StockBadge
                              qtyMilli={inStock}
                              minMilli={variants ? 0 : p.min_stock_milli}
                              track={tracked}
                              service={workshop}
                              count={
                                shopFields.has('stock')
                                  ? null
                                  : fieldText('stock', fieldRow, fieldCtx)
                              }
                            />
                          </TD>
                          <TD className="stack-actions sm:w-px sm:whitespace-nowrap">
                            <RowActions>
                              {receivable(p) && (
                                <Button
                                  size="sm"
                                  variant="soft"
                                  icon={<Package className="size-4" />}
                                  className="h-[34px] text-[13px]"
                                  aria-label={`Receive ${p.name}`}
                                  onClick={() => setReceiving(p)}
                                >
                                  Receive
                                </Button>
                              )}
                              {canEdit && (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  aria-label={`Edit ${p.name}`}
                                  title="Edit"
                                  className="h-[34px] w-[34px] px-0 max-sm:hidden"
                                  icon={<Pencil className="size-4" />}
                                  onClick={() => setEditing(p)}
                                />
                              )}
                            </RowActions>
                          </TD>
                        </TR>
                      )
                    })}
                  </tbody>
                </Table>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-muted max-sm:px-1">
                  <span>A to Z. A row opens the {words.column.toLowerCase()}.</span>
                  <span className="ml-auto flex items-center gap-1.5 max-lg:hidden">
                    <Kbd>↑</Kbd> <Kbd>↓</Kbd> move <Kbd>Enter</Kbd> open{' '}
                    {canEdit && (
                      <>
                        <Kbd>E</Kbd> edit <Kbd>R</Kbd> receive
                      </>
                    )}
                  </span>
                </div>
                <Pagination
                  offset={offset}
                  limit={PAGE}
                  total={page.data.total}
                  onChange={setOffset}
                />
              </>
            )}
            {selecting && selected.size > 0 && (
              <div className="sticky bottom-24 flex flex-wrap items-center justify-between gap-3 rounded-b-card border-t border-border bg-surface px-4 py-3 lg:bottom-0">
                <span className="text-sm font-medium">{selected.size} selected</span>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setSelected(new Set())}>
                    Clear
                  </Button>
                  <Button size="sm" onClick={() => setBulkOpen(true)}>
                    Change…
                  </Button>
                </div>
              </div>
            )}
          </ListFrame>
          {receiving && (
            <ReceiveStockDialog products={[receiving]} onClose={() => setReceiving(null)} />
          )}
        </>
      )}
      {bulkOpen && (
        <BulkEditDialog
          ids={selected}
          onClose={() => setBulkOpen(false)}
          onDone={() => {
            setBulkOpen(false)
            stopSelecting()
          }}
        />
      )}
      {editing && (
        <ProductForm
          key={formRun}
          product={editing === 'new' || editing === 'service' ? null : editing}
          ingredient={view === 'ingredients'}
          service={editing === 'service'}
          barcode={editing === 'new' ? (scanned ?? undefined) : undefined}
          like={editing === 'new' || editing === 'service' ? like : null}
          quiet
          // A scanned code is a new thing in hand: no old draft is offered for it.
          draft={(editing === 'new' && !scanned) || editing === 'service'}
          onClose={() => {
            closeForm()
            setLike(null)
          }}
          onSaved={(id) => {
            const kind = editing === 'new' || editing === 'service' ? editing : null
            closeForm()
            if (!kind) return
            void repo.get('products', id).then((product) => {
              if (product) setAdded({ product, kind })
              else navigate(`/products/${id}`)
            })
          }}
        />
      )}
      {added && (
        <AddedBox
          product={added.product}
          noun={
            view === 'ingredients'
              ? 'ingredient'
              : added.kind === 'service'
                ? 'service'
                : restaurant
                  ? 'item'
                  : 'product'
          }
          recipe={Boolean(restaurant && added.product.for_sale && added.product.kind !== 'group')}
          onAnother={() => {
            setLike(added.product)
            setFormRun((n) => n + 1)
            setEditing(added.kind)
            setAdded(null)
          }}
          onView={() => {
            setAdded(null)
            setLike(null)
            navigate(`/products/${added.product.id}`)
          }}
          onDone={() => {
            setAdded(null)
            setLike(null)
          }}
        />
      )}
    </div>
  )
}

interface ProductFormProps {
  product: ProductRow | null
  /** A new product opened from the Ingredients list: not sold, and counted. */
  ingredient?: boolean
  /** A new product opened as a service (workshops): not counted, sold by the job or hour. */
  service?: boolean
  /** A new product for a code that was scanned and not found: its barcode. */
  barcode?: string
  /** A new product for a name that was searched and not found. */
  name?: string
  /** The product added just before: a new one starts with its category, unit and VAT. */
  like?: ProductRow | null
  /** The caller shows its own box when a new product is added, so no toast for it. */
  quiet?: boolean
  /**
   * A new product from a list keeps what was typed as a draft on this device
   * (docs/plans/dialog-safety.md): X, Escape and Cancel close at once and nothing is lost.
   */
  draft?: boolean
  onClose: () => void
  onSaved: (id: string) => void
}

/**
 * After a new product is saved from a list: what was added, and the next step. "Add
 * another" comes first in the page so it takes focus (Enter adds the next one); it is
 * drawn last, on the right, like every main button.
 */
function AddedBox({
  product,
  noun,
  recipe,
  onAnother,
  onView,
  onDone,
}: {
  product: ProductRow
  noun: string
  /** A dish on a restaurant's menu: its recipe is set on its page. */
  recipe: boolean
  onAnother: () => void
  onView: () => void
  onDone: () => void
}) {
  const settings = useSettings()
  const lookups = useLookups()
  const category = lookups.categories.find((c) => c.id === product.category_id)?.name
  const details = [
    product.sell_price_paisa !== null ? money(product.sell_price_paisa, settings) : null,
    category,
  ].filter(Boolean)
  return (
    <Modal
      open
      onClose={onDone}
      title={`${noun.charAt(0).toUpperCase()}${noun.slice(1)} added`}
      footer={
        // A phone stacks the three, the main one on top; wider, one row with Done on the left.
        <div className="grid w-full gap-2 sm:flex sm:flex-row-reverse">
          <Button icon={<Plus className="size-4" />} onClick={onAnother}>
            Add another
          </Button>
          <Button variant="secondary" onClick={onView}>
            {recipe ? 'Add recipe' : `View ${noun}`}
          </Button>
          <Button variant="ghost" className="sm:mr-auto" onClick={onDone}>
            Done
          </Button>
        </div>
      }
    >
      <div className="flex items-start gap-3">
        <CheckCircle2 className="mt-0.5 size-6 shrink-0 animate-scale-in text-success" />
        <div className="min-w-0">
          <p className="truncate font-semibold">{product.name}</p>
          {details.length > 0 && <p className="mt-0.5 text-sm text-muted">{details.join(' · ')}</p>}
        </div>
      </div>
    </Modal>
  )
}

/** Waits for settings and lookups so the form's defaults (VAT, units) are right. */
export function ProductForm(props: ProductFormProps) {
  const settings = useSettings()
  const lookups = useLookups()
  const fieldGroups = useFieldGroups()
  const productId = props.product?.id ?? null
  const codes = useLive(
    async (repo) =>
      productId
        ? (await repo.list('product_codes', { where: { product_id: productId }, limit: 200 })).rows
        : [],
    [productId],
    ['product_codes'],
  ).data
  const groupId = props.product?.kind === 'group' ? props.product.id : null
  const variants = useLive(
    async (repo) =>
      groupId
        ? (
            await repo.list('products', {
              where: { parent_id: groupId },
              orderBy: { column: 'created_at' },
              limit: 1000,
            })
          ).rows
        : [],
    [groupId],
    ['products'],
  ).data
  if (settings === undefined || lookups.loading || codes === undefined || variants === undefined)
    return null
  return (
    <ProductFormInner
      {...props}
      settings={settings}
      lookups={lookups}
      codes={codes}
      variants={variants}
      fieldGroups={fieldGroups}
    />
  )
}

function ProductFormInner({
  product,
  ingredient = false,
  service = false,
  barcode: scannedBarcode,
  name: startName,
  like = null,
  quiet = false,
  draft = false,
  onClose,
  onSaved,
  settings,
  lookups,
  codes,
  variants,
  fieldGroups,
}: ProductFormProps & {
  settings: ReturnType<typeof useSettings>
  lookups: ReturnType<typeof useLookups>
  codes: ProductCodeRow[]
  variants: ProductRow[]
  fieldGroups: ReturnType<typeof useFieldGroups>
}) {
  const { groups } = fieldGroups
  // What the form calls the thing: a restaurant's menu has items, not products.
  const workshop = settings?.business_type === 'workshop'
  const noun = ingredient
    ? 'ingredient'
    : service
      ? 'service'
      : settings?.restaurant && settings.business_type === 'restaurant'
        ? 'item'
        : 'product'
  const Noun = noun.charAt(0).toUpperCase() + noun.slice(1)
  // Closing with edits asks first; a reload or a closed tab makes the browser ask. A new
  // product from a list keeps a draft instead, so it closes at once (see below).
  const draftOn = !product && draft
  const [dirty, setDirty] = useState(false)
  const [leaving, setLeaving] = useState(false)
  useUnsavedWarning(dirty && !leaving && !draftOn)
  // A group keeps no stock itself: its variants' tracking is what the form shows.
  const seed = product?.kind === 'group' ? (variants[0] ?? null) : product
  const canHaveVariants = groups.has('variants') && (!product || product.kind === 'group')
  const [hasVariants, setHasVariants] = useState(product?.kind === 'group')
  const [optionRows, setOptionRows] = useState(() => optionDrafts(product))
  const [variantEdits, setVariantEdits] = useState<Record<string, VariantEdit>>({})
  const [trackSerials, setTrackSerials] = useState(seed?.track_serials ?? false)
  const [warranty, setWarranty] = useState(seed?.warranty_months?.toString() ?? '')
  const [openingSerials, setOpeningSerials] = useState('')
  const warrantyMonths = /^\d+$/.test(warranty) && Number(warranty) > 0 ? Number(warranty) : null
  const [brandId, setBrandId] = useState(product?.brand_id ?? '')
  const [supplierId, setSupplierId] = useState(product?.supplier_id ?? '')
  const [newBrandName, setNewBrandName] = useState('')
  const [manufacturer, setManufacturer] = useState(product?.manufacturer ?? '')
  const [mrp, setMrp] = useState<number | null>(product?.mrp_paisa ?? null)
  const [wholesale, setWholesale] = useState<number | null>(product?.wholesale_price_paisa ?? null)
  const [minPrice, setMinPrice] = useState<number | null>(product?.min_price_paisa ?? null)
  const [plu, setPlu] = useState(product?.plu_code ?? '')
  const [hsCode, setHsCode] = useState(product?.hs_code ?? '')
  const [shelf, setShelf] = useState(product?.shelf_location ?? '')
  const [tags, setTags] = useState(product?.tags ?? '')
  const [reorderQty, setReorderQty] = useState<number | null>(product?.reorder_qty_milli ?? 0)
  const [trackBatches, setTrackBatches] = useState(seed?.track_batches ?? false)
  const [trackExpiry, setTrackExpiry] = useState(seed?.track_expiry ?? false)
  const [shelfLife, setShelfLife] = useState(seed?.shelf_life_days?.toString() ?? '')
  const [shelfUnit, setShelfUnit] = useState<ShelfLifeUnit>(seed?.shelf_life_unit ?? 'day')
  const [openingBatch, setOpeningBatch] = useState({ no: '', mfg: '', expiry: '', auto: false })
  const shelfLifeDays = /^\d+$/.test(shelfLife) && Number(shelfLife) > 0 ? Number(shelfLife) : null
  const lifeRule = { amount: shelfLifeDays, unit: shelfUnit }
  // With a shelf life, the expiry follows the manufacture date until someone types one.
  const onOpeningMfg = (mfg: string) =>
    setOpeningBatch((b) => ({ ...b, mfg, ...followExpiry(mfg, b, lifeRule) }))
  // The mfg date may come first: a shelf life typed or changed after it works the expiry out
  // again (an expiry typed by hand still stays).
  const onShelf = (amountText: string, unit: ShelfLifeUnit, tracking = trackExpiry) => {
    setShelfLife(amountText)
    setShelfUnit(unit)
    const amount = /^\d+$/.test(amountText) && Number(amountText) > 0 ? Number(amountText) : null
    setOpeningBatch((b) => ({
      ...b,
      ...followExpiry(b.mfg, b, { amount: tracking ? amount : null, unit }),
    }))
  }
  // Every stored attribute is kept, including ones of groups that are hidden now.
  const [attrs, setAttrs] = useState<Record<string, JsonValue | undefined>>(() =>
    product?.attributes &&
    typeof product.attributes === 'object' &&
    !Array.isArray(product.attributes)
      ? { ...product.attributes }
      : {},
  )
  const [packs, setPacks] = useState<PackDraft[]>(() => packDrafts(codes))
  const attributeSections = attributeFields(groups, fieldGroups.custom)
  const repo = useRepo()
  const { current } = useWorkspace()
  const toast = useToast()
  const [name, setName] = useState(product?.name ?? startName ?? '')
  const [sku, setSku] = useState(product?.sku ?? '')
  const [barcode, setBarcode] = useState(product?.barcode ?? scannedBarcode ?? '')
  // A scanned barcode of a new product: the open product databases may know its name and
  // brand. Whatever they say only fills what is still empty, and the shop checks it.
  const { platform } = useSession()
  const [suggestedBy, setSuggestedBy] = useState<string | null>(null)
  const lookUp = async (code: string): Promise<BarcodeSuggestion | null> => {
    // Only for a new product whose name is still to be typed: otherwise there is nothing to fill.
    if (product || !current || name.trim()) return null
    try {
      const api = createApi(platform.apiUrl, platform.supabase)
      return (await api.lookupBarcode(current.tenant_id, code)).suggestion
    } catch {
      // Offline, not a code the databases take, or they did not answer: the form stays empty.
      return null
    }
  }
  const applySuggestion = (suggestion: BarcodeSuggestion | null) => {
    if (!suggestion || name.trim()) return
    setName(suggestedName(suggestion))
    setSuggestedBy(suggestion.source)
    if (suggestion.brand && groups.has('brand') && !brandId) {
      const wanted = suggestion.brand.toLowerCase()
      const known = lookups.brands.find((b) => b.name.trim().toLowerCase() === wanted)
      setBrandId(known?.id ?? '__new')
      if (!known) setNewBrandName(suggestion.brand)
    }
  }
  /** After the form's own Barcode button reads a code. */
  const suggest = (code: string) => void lookUp(code).then(applySuggestion)
  // Opened for a code the scanner did not find: look it up once.
  const lookUpScanned = useEffectEvent(() => (scannedBarcode ? lookUp(scannedBarcode) : null))
  const onScannedSuggestion = useEffectEvent(applySuggestion)
  useEffect(() => {
    let closed = false
    void Promise.resolve(lookUpScanned()).then((found) => {
      if (!closed) onScannedSuggestion(found)
    })
    return () => {
      closed = true
    }
  }, [scannedBarcode])
  const [price, setPrice] = useState<number | null>(product?.sell_price_paisa ?? null)
  const [cost, setCost] = useState<number | null>(product?.cost_price_paisa ?? null)
  const [includesTax, setIncludesTax] = useState(
    product?.price_includes_tax ??
      like?.price_includes_tax ??
      settings?.prices_include_tax ??
      false,
  )
  const [taxRateId, setTaxRateId] = useState(
    product?.tax_rate_id ??
      (like ? (like.tax_rate_id ?? '') : (settings?.default_tax_rate_id ?? '')),
  )
  const currency = settings?.currency_symbol ?? 'Rs.'
  // Display only: the margin on one unit, against the price before VAT.
  const margin = (() => {
    if (price === null || cost === null || price <= 0) return null
    const rateBp = lookups.taxRates.find((t) => t.id === taxRateId)?.rate_bp ?? 0
    const net = includesTax && rateBp ? mulDivRound(price, 10_000, 10_000 + rateBp) : price
    const paisa = net - cost
    return { paisa, percent: ((paisa / net) * 100).toFixed(1) }
  })()
  // A service is sold by the job unless the workshop says otherwise.
  const jobUnit = lookups.units.find((u) => u.symbol.toLowerCase() === 'job')?.id ?? ''
  const [unitId, setUnitId] = useState(
    product?.unit_id ?? like?.unit_id ?? (service ? jobUnit : ''),
  )
  const [categoryId, setCategoryId] = useState(product?.category_id ?? like?.category_id ?? '')
  const [newCategoryName, setNewCategoryName] = useState('')
  const [minStock, setMinStock] = useState<number | null>(product?.min_stock_milli ?? 0)
  // A restaurant's new product is a dish unless told otherwise: nothing to count.
  const [trackStock, setTrackStock] = useState(
    seed?.track_stock ?? (!service && (ingredient || settings?.business_type !== 'restaurant')),
  )
  // Restaurant mode: on the menu, or an ingredient the kitchen uses.
  const [forSale, setForSale] = useState(product?.for_sale ?? !ingredient)
  const [active, setActive] = useState(product?.is_active ?? true)
  const [imagePath, setImagePath] = useState(product?.image_path ?? null)
  const [station, setStation] = useState(
    seed?.kitchen_station ?? like?.kitchen_station ?? 'kitchen',
  )
  // What the guest's menu says beside the dish (docs/plans/guest-menu.md).
  const [diet, setDiet] = useState(seed?.diet ?? null)
  const [spicy, setSpicy] = useState(seed?.spicy ?? false)
  const [description, setDescription] = useState(product?.description ?? '')
  const [opening, setOpening] = useState<number | null>(null)
  const [openingLocation, setOpeningLocation] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [newUnitSymbol, setNewUnitSymbol] = useState('')
  const [newUnitName, setNewUnitName] = useState('')
  const [newUnitDecimal, setNewUnitDecimal] = useState(false)
  const creatingUnit = unitId === '__new'
  const unit = creatingUnit ? null : lookups.unit(unitId)
  const allowsDecimal = creatingUnit ? newUnitDecimal : (unit?.allows_decimal ?? false)
  // A known symbol ("dz", "pkt") fills in the name and decimals.
  const fillNewUnit = (symbol: string) => {
    const preset = presetFor(symbol)
    if (!preset) return
    if (!newUnitName.trim()) setNewUnitName(preset.name)
    setNewUnitDecimal(preset.allowsDecimal)
  }

  // The draft: every field as typed, one per business and kind on this device. A photo
  // already uploaded is kept as its path.
  const keyOfDraft =
    draftOn && current
      ? draftKey(
          current.tenant_id,
          'product',
          ingredient ? 'ingredient' : service ? 'service' : 'new',
        )
      : null
  const form = {
    name,
    sku,
    barcode,
    description,
    price,
    cost,
    includesTax,
    taxRateId,
    unitId,
    categoryId,
    newCategoryName,
    minStock,
    trackStock,
    forSale,
    active,
    imagePath,
    station,
    diet,
    spicy,
    opening,
    openingLocation,
    newUnitSymbol,
    newUnitName,
    newUnitDecimal,
    hasVariants,
    optionRows,
    variantEdits,
    trackSerials,
    warranty,
    openingSerials,
    brandId,
    supplierId,
    newBrandName,
    manufacturer,
    mrp,
    wholesale,
    minPrice,
    plu,
    hsCode,
    shelf,
    tags,
    reorderQty,
    trackBatches,
    trackExpiry,
    shelfLife,
    shelfUnit,
    openingBatch,
    attrs,
    packs,
  }
  const formText = JSON.stringify(form)
  const [startText] = useState(formText)
  const touched = draftOn && formText !== startText
  const [offer, setOffer] = useState(() =>
    keyOfDraft ? readDraft(keyOfDraft, parseFormDraft) : null,
  )
  useEffect(() => {
    if (!keyOfDraft || offer) return
    const timer = window.setTimeout(() => {
      if (touched) saveDraft(keyOfDraft, JSON.parse(formText) as unknown)
      else clearDraft(keyOfDraft)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [keyOfDraft, offer, touched, formText])
  const continueDraft = () => {
    if (!offer) return
    const { values: v } = mergeFormDraft(form, offer.value, {
      price: 'number',
      cost: 'number',
      mrp: 'number',
      wholesale: 'number',
      minPrice: 'number',
      minStock: 'number',
      reorderQty: 'number',
      opening: 'number',
      openingLocation: 'string',
      imagePath: 'string',
      diet: 'string',
    })
    setName(v.name)
    setSku(v.sku)
    setBarcode(v.barcode)
    setDescription(v.description)
    setPrice(v.price)
    setCost(v.cost)
    setIncludesTax(v.includesTax)
    setTaxRateId(v.taxRateId)
    setUnitId(v.unitId)
    setCategoryId(v.categoryId)
    setNewCategoryName(v.newCategoryName)
    setMinStock(v.minStock)
    setTrackStock(v.trackStock)
    setForSale(v.forSale)
    setActive(v.active)
    setImagePath(v.imagePath)
    // Words the server only takes from a fixed list: anything else keeps the form's own.
    if (['kitchen', 'bar', 'none'].includes(v.station)) setStation(v.station)
    if (v.diet === null || ['veg', 'non_veg', 'egg'].includes(v.diet)) setDiet(v.diet)
    setSpicy(v.spicy)
    setOpening(v.opening)
    setOpeningLocation(v.openingLocation)
    setNewUnitSymbol(v.newUnitSymbol)
    setNewUnitName(v.newUnitName)
    setNewUnitDecimal(v.newUnitDecimal)
    setHasVariants(v.hasVariants && canHaveVariants)
    setOptionRows(v.optionRows)
    setVariantEdits(v.variantEdits)
    setTrackSerials(v.trackSerials)
    setWarranty(v.warranty)
    setOpeningSerials(v.openingSerials)
    setBrandId(v.brandId)
    setSupplierId(v.supplierId)
    setNewBrandName(v.newBrandName)
    setManufacturer(v.manufacturer)
    setMrp(v.mrp)
    setWholesale(v.wholesale)
    setMinPrice(v.minPrice)
    setPlu(v.plu)
    setHsCode(v.hsCode)
    setShelf(v.shelf)
    setTags(v.tags)
    setReorderQty(v.reorderQty)
    setTrackBatches(v.trackBatches)
    setTrackExpiry(v.trackExpiry)
    setShelfLife(v.shelfLife)
    if (['day', 'month', 'year'].includes(v.shelfUnit)) setShelfUnit(v.shelfUnit)
    setOpeningBatch(v.openingBatch)
    setAttrs(v.attrs)
    setPacks(v.packs)
    setOffer(null)
  }
  const startNew = () => {
    if (keyOfDraft) clearDraft(keyOfDraft)
    setOffer(null)
  }
  const close = () => {
    if (!draftOn) return dirty ? setLeaving(true) : onClose()
    if (keyOfDraft && !offer && touched) {
      saveDraft(keyOfDraft, form)
      toast.info('Draft kept', {
        detail: `${name.trim() || `The new ${noun}`} · open Add ${noun} again to go on`,
      })
    }
    onClose()
  }

  const save = async () => {
    if (!current) return
    if (!name.trim()) return toast.error('A name is required')
    // No SKU typed: one is made from the name, the first that nothing else uses.
    const code =
      sku.trim() ||
      (await nextSku(name, async (c) => {
        const other = await repo.products.lookupCode(c)
        return !!other && other.product.id !== product?.id
      }))
    // An ingredient is never sold, so it has no selling price.
    if (price === null && forSale) return toast.error('Enter a valid price')
    const sellPrice = price ?? 0
    if (minPrice !== null && minPrice > sellPrice)
      return toast.error('The minimum price is above the selling price')
    if (plu.trim()) {
      const other = (
        await repo.list('products', { where: { plu_code: normalizePlu(plu.trim()) }, limit: 2 })
      ).rows.find((x) => x.id !== product?.id && !x.deleted_at)
      if (other) return toast.error(`Scale code ${plu.trim()} already belongs to ${other.name}`)
    }
    if (creatingUnit && (!newUnitSymbol.trim() || !newUnitName.trim())) {
      return toast.error('Enter the new unit’s symbol and name')
    }
    if (creatingUnit && findUnit(lookups.units, newUnitSymbol)) {
      return toast.error(`A unit “${newUnitSymbol.trim()}” already exists: pick it from the list`)
    }
    if (brandId === '__new' && !newBrandName.trim())
      return toast.error('Enter the new brand’s name')
    const livePacks = packs.filter((p) => !p.removed)
    for (const p of livePacks) {
      if (!p.code.trim()) return toast.error('Each pack needs a barcode')
      if (!p.qtyMilli || p.qtyMilli <= 0) return toast.error(`Enter the quantity in “${p.code}”`)
    }
    const packCodes = livePacks.map((p) => p.code.trim())
    if (new Set(packCodes).size !== packCodes.length)
      return toast.error('A pack barcode is repeated')
    if (barcode.trim() && packCodes.includes(barcode.trim())) {
      return toast.error('A pack barcode is the same as the product barcode')
    }
    for (const code of packCodes) {
      const other = await repo.products.lookupCode(code)
      if (other && other.product.id !== product?.id) {
        return toast.error(`Barcode ${code} already belongs to ${other.product.name}`)
      }
    }
    const variantOptions = draftOptions(optionRows)
    const variantRows = hasVariants
      ? variantDrafts(variantOptions, variants, variantEdits, code).filter((d) => d.include)
      : []
    if (hasVariants) {
      if (variantRows.length === 0)
        return toast.error('Add an option with values, like Size: S, M, L')
      if (variantRows.length > MAX_VARIANTS)
        return toast.error(`At most ${MAX_VARIANTS} variants per product`)
      const skus = variantRows.map((d) => d.sku.trim().toLowerCase())
      if (skus.some((x) => !x)) return toast.error('Each variant needs a SKU')
      if (new Set([...skus, code.toLowerCase()]).size !== skus.length + 1)
        return toast.error('Variant SKUs must differ from each other and from the product SKU')
      const codesToCheck = variantRows.flatMap((d) =>
        [d.sku.trim(), d.barcode.trim()].filter(Boolean).map((code) => ({ code, d })),
      )
      const barcodes = variantRows.map((d) => d.barcode.trim()).filter(Boolean)
      if (new Set(barcodes).size !== barcodes.length)
        return toast.error('A variant barcode is repeated')
      for (const { code, d } of codesToCheck) {
        const other = await repo.products.lookupCode(code)
        if (other && other.product.id !== d.existing?.id && other.product.id !== product?.id) {
          return toast.error(`${code} already belongs to ${other.product.name}`)
        }
      }
    }
    const serialsIn = parseSerials(openingSerials)
    if (serialsIn.duplicates.length > 0)
      return toast.error(`Serial ${serialsIn.duplicates[0]} is repeated`)
    setBusy(true)
    try {
      const ctx = { tenantId: current.tenant_id }
      const ops = []
      let brand = brandId && brandId !== '__new' ? brandId : null
      if (brandId === '__new') {
        const existing = lookups.brands.find(
          (b) => b.name.trim().toLowerCase() === newBrandName.trim().toLowerCase(),
        )
        if (existing) brand = existing.id
        else {
          const b = newBrand(ctx, { name: newBrandName.trim() })
          ops.push(insertOp('brands', b))
          brand = b.id
        }
      }
      let category = categoryId || null
      if (categoryId === '__new') {
        // After the last one: a restaurant's menu shows its sections in this order.
        const cat = newCategory(ctx, {
          name: newCategoryName.trim(),
          sort_order: nextSortOrder(lookups.categories),
        })
        ops.push(insertOp('categories', cat))
        category = cat.id
      }
      let unitRef = unitId || null
      if (creatingUnit) {
        const u = newUnit(ctx, {
          name: newUnitName.trim(),
          symbol: newUnitSymbol.trim(),
          allows_decimal: newUnitDecimal,
        })
        ops.push(insertOp('units', u))
        unitRef = u.id
      }
      const fields = {
        name: name.trim(),
        sku: code,
        barcode: barcode.trim() || null,
        description: description.trim() || null,
        image_path: imagePath,
        sell_price_paisa: sellPrice,
        cost_price_paisa: cost,
        price_includes_tax: includesTax,
        tax_rate_id: taxRateId || null,
        unit_id: unitRef,
        category_id: category,
        min_stock_milli: minStock ?? 0,
        track_stock: trackStock,
        kitchen_station: station,
        for_sale: forSale,
        diet: forSale ? diet : null,
        spicy: forSale && spicy,
        track_batches: trackStock && trackBatches && !trackSerials,
        track_expiry: trackStock && trackBatches && !trackSerials && trackExpiry,
        shelf_life_days: shelfLifeDays,
        // Days are stored as null, the meaning every product had before units.
        shelf_life_unit: shelfLifeDays && shelfUnit !== 'day' ? shelfUnit : null,
        track_serials: trackStock && trackSerials,
        warranty_months: trackSerials ? warrantyMonths : null,
        is_active: active,
        brand_id: brand,
        supplier_id: supplierId || null,
        manufacturer: manufacturer.trim() || null,
        mrp_paisa: mrp,
        wholesale_price_paisa: wholesale,
        min_price_paisa: minPrice,
        plu_code: hasVariants || !plu.trim() ? null : normalizePlu(plu.trim()),
        hs_code: hsCode.trim() || null,
        shelf_location: shelf.trim() || null,
        tags: parseTags(tags).join(', ') || null,
        reorder_qty_milli: reorderQty ?? 0,
        attributes: cleanAttributes(attrs),
      }
      if (hasVariants) {
        const loc = openingLocation ?? lookups.activeLocations[0]?.id
        const groupFields = {
          ...fields,
          barcode: null,
          kind: 'group' as const,
          track_stock: false,
          track_batches: false,
          track_expiry: false,
          track_serials: false,
          variant_options: variantOptions,
        }
        let groupId: string
        if (product) {
          ops.push(updateOp('products', product, groupFields))
          groupId = product.id
        } else {
          const g = newProduct(ctx, groupFields)
          ops.push(insertOp('products', g))
          groupId = g.id
        }
        const after = { ...fields, variant_options: variantOptions }
        for (const d of variantRows) {
          const own = { sku: d.sku.trim(), barcode: d.barcode.trim() || null }
          if (d.existing) {
            const changes: Record<string, unknown> = {
              ...(product ? variantChanges(d.existing, product, after) : null),
            }
            if (own.sku !== d.existing.sku) changes.sku = own.sku
            if (own.barcode !== d.existing.barcode) changes.barcode = own.barcode
            if (variantEdits[d.key]?.pricePaisa !== undefined)
              changes.sell_price_paisa = d.pricePaisa ?? price
            if (Object.keys(changes).length > 0) ops.push(updateOp('products', d.existing, changes))
            continue
          }
          const v = newProduct(ctx, {
            ...fields,
            ...own,
            name: variantName(fields.name, d.values, variantOptions),
            sell_price_paisa: d.pricePaisa ?? sellPrice,
            kind: 'variant',
            parent_id: groupId,
            variant_values: d.values,
          })
          ops.push(insertOp('products', v))
          if (trackStock && !trackSerials && d.openingMilli && d.openingMilli > 0 && loc) {
            ops.push(
              insertOp(
                'stock_movements',
                newMovement(ctx, {
                  product_id: v.id,
                  location_id: loc,
                  type: 'opening',
                  qty_milli: d.openingMilli,
                  unit_cost_paisa: cost,
                  notes: 'Opening stock',
                }),
              ),
            )
          }
        }
        await repo.commit(ops)
        if (keyOfDraft) clearDraft(keyOfDraft)
        if (product || !quiet) toast.success(product ? `${Noun} updated` : `${Noun} added`)
        onSaved(groupId)
        return
      }
      let id: string
      if (product) {
        ops.push(updateOp('products', product, fields))
        id = product.id
      } else {
        const row = newProduct(ctx, fields)
        ops.push(insertOp('products', row))
        id = row.id
        const loc = openingLocation ?? lookups.activeLocations[0]?.id
        if (trackStock && trackSerials && serialsIn.serials.length > 0 && loc) {
          ops.push(
            insertOp(
              'stock_movements',
              newMovement(ctx, {
                product_id: id,
                location_id: loc,
                type: 'opening',
                qty_milli: serialsIn.serials.length * 1000,
                unit_cost_paisa: cost,
                notes: 'Opening stock',
              }),
            ),
          )
          for (const serial of serialsIn.serials) {
            ops.push(serialOp(ctx, id, serial, null, { status: 'in_stock', location_id: loc }))
          }
        } else if (trackStock && !trackSerials && opening && opening > 0 && loc) {
          let openingBatchId: string | null = null
          if (trackBatches && openingBatch.no.trim()) {
            const b = batchOp(
              ctx,
              id,
              {
                batchNo: openingBatch.no,
                mfgDate: openingBatch.mfg || null,
                expiryDate: openingBatch.expiry || null,
                mrpPaisa: mrp,
                costPaisa: cost,
              },
              null,
            )
            openingBatchId = b.id
            if (b.op) ops.push(b.op)
          }
          ops.push(
            insertOp(
              'stock_movements',
              newMovement(ctx, {
                product_id: id,
                location_id: loc,
                type: 'opening',
                qty_milli: opening,
                unit_cost_paisa: cost,
                notes: 'Opening stock',
                batch_id: openingBatchId,
              }),
            ),
          )
        }
      }
      for (const p of packs) {
        const values = {
          code: p.code.trim(),
          label: p.label.trim() || null,
          qty_milli: p.qtyMilli ?? 1000,
          sell_price_paisa: p.pricePaisa,
        }
        const existing = p.id ? codes.find((c) => c.id === p.id) : undefined
        if (existing && p.removed) ops.push(deleteOp('product_codes', existing.id))
        else if (existing) {
          const changed = (Object.keys(values) as (keyof typeof values)[]).some(
            (k) => existing[k] !== values[k],
          )
          if (changed) ops.push(updateOp('product_codes', existing, values))
        } else if (!p.removed) {
          ops.push(insertOp('product_codes', newProductCode(ctx, { product_id: id, ...values })))
        }
      }
      await repo.commit(ops)
      if (keyOfDraft) clearDraft(keyOfDraft)
      if (product || !quiet) toast.success(product ? `${Noun} updated` : `${Noun} added`)
      onSaved(id)
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={close}
      title={product ? `Edit ${noun}` : `Add ${noun}`}
      size="lg"
      unsaved={draftOn ? touched && `This ${noun} is not added yet` : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button loading={busy} disabled={!!offer} onClick={() => void save()}>
            {product ? 'Save changes' : `Add ${noun}`}
          </Button>
        </>
      }
    >
      <ConfirmDialog
        open={leaving}
        title="Discard your changes?"
        message={`What you typed for this ${noun} has not been saved.`}
        confirmLabel="Discard"
        danger
        onClose={() => setLeaving(false)}
        onConfirm={onClose}
      />
      {offer && (
        <div className="mb-4">
          <DraftBanner
            savedAt={offer.savedAt}
            summary={
              typeof offer.value.name === 'string' && offer.value.name.trim()
                ? offer.value.name.trim()
                : `A new ${noun}`
            }
            onStartNew={startNew}
            onContinue={continueDraft}
          />
        </div>
      )}
      <div
        inert={!!offer}
        className={cx('grid gap-4 sm:grid-cols-2', offer && 'opacity-50')}
        onChangeCapture={() => setDirty(true)}
      >
        <Field
          label="Name"
          className="sm:col-span-2"
          hint={suggestedBy ? `Suggested by ${suggestedBy}. Check it before saving.` : undefined}
        >
          {(id) => (
            <Input
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus={finePointer}
            />
          )}
        </Field>
        <Field label="SKU" hint="Your own code. Left empty, one is made from the name.">
          {(id) => (
            <Input
              id={id}
              value={sku}
              placeholder={name.trim() ? skuFromName(name) : undefined}
              onChange={(e) => setSku(e.target.value)}
            />
          )}
        </Field>
        {!hasVariants && (
          <Field label="Barcode" hint="Scan it here.">
            {(id) => (
              <div className="relative">
                <Input
                  id={id}
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  className="pr-10"
                />
                <ScanButton
                  mode="single"
                  title={`Scan the ${noun}’s barcode`}
                  onCode={(code) => {
                    setBarcode(code)
                    suggest(code)
                    return { ok: true, message: code }
                  }}
                  className="absolute right-1 top-1/2 -translate-y-1/2"
                />
              </div>
            )}
          </Field>
        )}
        {forSale && (
          <Field label="Selling price">
            {(id) => (
              <MoneyInput
                id={id}
                paisa={price}
                onValue={setPrice}
                placeholder="0.00"
                prefix={currency}
                className="font-semibold"
              />
            )}
          </Field>
        )}
        <Field label="Cost price (optional)">
          {(id) => (
            <>
              <MoneyInput
                id={id}
                paisa={cost}
                onValue={setCost}
                placeholder="0.00"
                prefix={currency}
                aria-describedby={margin ? `${id}-margin` : undefined}
              />
              {margin && (
                <p
                  id={`${id}-margin`}
                  className={cx(
                    'mt-1 text-xs font-medium',
                    margin.paisa < 0 ? 'text-danger' : 'text-success',
                  )}
                >
                  {margin.paisa < 0 ? 'Loss' : 'Margin'} {money(Math.abs(margin.paisa), settings)} ·{' '}
                  {margin.percent}%{includesTax ? ' (before VAT)' : ''}
                </p>
              )}
            </>
          )}
        </Field>
        {forSale && (
          <Field label="VAT">
            {(id) => (
              <Select id={id} value={taxRateId} onChange={(e) => setTaxRateId(e.target.value)}>
                <option value="">No VAT</option>
                {lookups.taxRates
                  .filter((t) => t.is_active)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
        )}
        {forSale && (
          <div className="flex items-end pb-2">
            <Checkbox
              label="Price includes VAT"
              checked={includesTax}
              onChange={(e) => setIncludesTax(e.target.checked)}
            />
          </div>
        )}
        <Field label="Unit">
          {(id) => (
            <Select id={id} value={unitId} onChange={(e) => setUnitId(e.target.value)}>
              <option value="">—</option>
              {lookups.units.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.symbol})
                </option>
              ))}
              <option value="__new">+ New unit…</option>
            </Select>
          )}
        </Field>
        <Field label="Category">
          {(id) => (
            <Select id={id} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">—</option>
              {lookups.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
              <option value="__new">+ New category…</option>
            </Select>
          )}
        </Field>
        {creatingUnit && (
          <div className="grid gap-3 rounded-xl border border-border p-3 sm:col-span-2 sm:grid-cols-[8rem_1fr_auto] sm:items-end">
            <Field label="Unit symbol">
              {(id) => (
                <Input
                  id={id}
                  value={newUnitSymbol}
                  placeholder="box"
                  onChange={(e) => setNewUnitSymbol(e.target.value)}
                  onBlur={() => fillNewUnit(newUnitSymbol)}
                />
              )}
            </Field>
            <Field label="Unit name">
              {(id) => (
                <Input
                  id={id}
                  value={newUnitName}
                  placeholder="Box"
                  onChange={(e) => setNewUnitName(e.target.value)}
                />
              )}
            </Field>
            <Checkbox
              className="pb-2"
              label="Allows decimals"
              checked={newUnitDecimal}
              onChange={(e) => setNewUnitDecimal(e.target.checked)}
            />
          </div>
        )}
        {categoryId === '__new' && (
          <Field label="New category name" className="sm:col-span-2">
            {(id) => (
              <Input
                id={id}
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
              />
            )}
          </Field>
        )}
        <Field label="Usual supplier (optional)" hint="Who you usually buy it from.">
          {(id) => <SupplierPicker id={id} value={supplierId} onChange={setSupplierId} optional />}
        </Field>
        {groups.has('brand') && (
          <>
            <BrandField
              lookups={lookups}
              brandId={brandId}
              onBrand={setBrandId}
              newBrandName={newBrandName}
              onNewBrandName={setNewBrandName}
            />
            <Field label="Manufacturer">
              {(id) => (
                <Input
                  id={id}
                  value={manufacturer}
                  onChange={(e) => setManufacturer(e.target.value)}
                />
              )}
            </Field>
          </>
        )}
        {groups.has('pricing') && (
          <Field label="MRP" hint="Printed maximum retail price (with VAT).">
            {(id) => (
              <MoneyInput
                id={id}
                paisa={mrp}
                onValue={setMrp}
                placeholder="0.00"
                prefix={currency}
              />
            )}
          </Field>
        )}
        {groups.has('wholesale') && (
          <>
            <Field label="Wholesale price" hint="Used for wholesale customers.">
              {(id) => (
                <MoneyInput
                  id={id}
                  paisa={wholesale}
                  onValue={setWholesale}
                  placeholder="0.00"
                  prefix={currency}
                />
              )}
            </Field>
            <Field label="Minimum price" hint="Staff can’t sell below this (per unit).">
              {(id) => (
                <MoneyInput
                  id={id}
                  paisa={minPrice}
                  onValue={setMinPrice}
                  placeholder="0.00"
                  prefix={currency}
                />
              )}
            </Field>
          </>
        )}
        {groups.has('scale') && !hasVariants && (
          <Field label="Scale item code (PLU)" hint="The code programmed into your label scale.">
            {(id) => (
              <Input
                id={id}
                inputMode="numeric"
                value={plu}
                placeholder="123"
                onChange={(e) => setPlu(e.target.value.replace(/\D/g, '').slice(0, 8))}
              />
            )}
          </Field>
        )}
        {groups.has('trade') && (
          <Field label="HS code">
            {(id) => (
              <Input
                id={id}
                value={hsCode}
                placeholder="3004.90"
                onChange={(e) => setHsCode(e.target.value)}
              />
            )}
          </Field>
        )}
        {groups.has('shelf') && (
          <>
            <Field label="Shelf / rack">
              {(id) => (
                <Input
                  id={id}
                  value={shelf}
                  placeholder="A-3"
                  onChange={(e) => setShelf(e.target.value)}
                />
              )}
            </Field>
            <Field label="Tags" hint="Separate with commas.">
              {(id) => (
                <Input
                  id={id}
                  value={tags}
                  placeholder="otc, fever"
                  onChange={(e) => setTags(e.target.value)}
                />
              )}
            </Field>
          </>
        )}
        {settings?.restaurant && (
          <Switch
            className="sm:col-span-2"
            label="Sold to customers"
            description={
              forSale
                ? 'On the menu: waiters can order it and it can be billed.'
                : 'An ingredient: kept in stock and used in recipes, never on the menu or a bill.'
            }
            checked={forSale}
            onChange={(e) => setForSale(e.target.checked)}
          />
        )}
        {(groups.has('photo') || imagePath) && forSale && (
          <>
            <SectionTitle>Photo</SectionTitle>
            <PhotoField path={imagePath} onChange={setImagePath} name={name} />
          </>
        )}
        <SectionTitle>Stock</SectionTitle>
        {workshop ? (
          <Switch
            label="This is a service"
            description="Labour or a fixed job: billed like a part, nothing is counted in stock"
            checked={!trackStock}
            onChange={(e) => {
              setTrackStock(!e.target.checked)
              if (e.target.checked && !unitId) setUnitId(jobUnit)
            }}
          />
        ) : (
          <Switch
            label="Track stock"
            description="Count what comes in and goes out"
            checked={trackStock}
            onChange={(e) => setTrackStock(e.target.checked)}
          />
        )}
        <Switch
          label="Active"
          description="Shows in billing and search"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
        />
        {settings?.restaurant && forSale && (
          <Field label="Orders go to" hint="Which kitchen ticket an order of this is printed on.">
            {(id) => (
              <Select
                id={id}
                value={station}
                onChange={(e) => setStation(e.target.value as typeof station)}
              >
                <option value="kitchen">Kitchen</option>
                <option value="bar">Bar</option>
                <option value="none">No ticket</option>
              </Select>
            )}
          </Field>
        )}
        {settings?.restaurant && forSale && (
          <>
            <Field
              label="Mark on the guest menu"
              hint="The dot beside the dish when guests order from the table."
            >
              {(id) => (
                <Select
                  id={id}
                  value={diet ?? ''}
                  onChange={(e) => setDiet((e.target.value || null) as typeof diet)}
                >
                  <option value="">No mark</option>
                  <option value="veg">Veg</option>
                  <option value="non_veg">Non-veg</option>
                  <option value="egg">Egg</option>
                </Select>
              )}
            </Field>
            <Switch
              label="Spicy"
              description="Shown beside the dish on the guest menu"
              checked={spicy}
              onChange={(e) => setSpicy(e.target.checked)}
            />
          </>
        )}
        {trackStock && groups.has('serials') && (
          <div className="grid animate-fade-in gap-3 rounded-xl border border-primary/15 bg-primary-light/50 p-3 sm:col-span-2 sm:grid-cols-2 sm:items-end">
            <Checkbox
              label="Track serial numbers (IMEI)"
              checked={trackSerials}
              onChange={(e) => setTrackSerials(e.target.checked)}
            />
            <Field label="Warranty (months)" hint="Printed on the bill from the sale date.">
              {(id) => (
                <Input
                  id={id}
                  inputMode="numeric"
                  value={warranty}
                  disabled={!trackSerials}
                  onChange={(e) => setWarranty(e.target.value.replace(/\D/g, ''))}
                />
              )}
            </Field>
          </div>
        )}
        {trackStock && !trackSerials && groups.has('batches') && (
          <div className="grid animate-fade-in gap-3 rounded-xl border border-primary/15 bg-primary-light/50 p-3 sm:col-span-2 sm:grid-cols-2 sm:items-center">
            <div className="grid gap-3">
              <Checkbox
                label="Track batches"
                checked={trackBatches}
                onChange={(e) => setTrackBatches(e.target.checked)}
              />
              <Checkbox
                label="Track expiry dates"
                checked={trackBatches && trackExpiry}
                disabled={!trackBatches}
                onChange={(e) => {
                  setTrackExpiry(e.target.checked)
                  onShelf(shelfLife, shelfUnit, e.target.checked)
                }}
              />
            </div>
            <Field label="Shelf life" hint="Fills in expiry from the mfg date.">
              {(id) => (
                <div className="grid grid-cols-[5rem_minmax(0,1fr)] gap-2">
                  <Input
                    id={id}
                    inputMode="numeric"
                    className="text-right tabular"
                    value={shelfLife}
                    disabled={!trackBatches || !trackExpiry}
                    onChange={(e) => onShelf(e.target.value.replace(/\D/g, ''), shelfUnit)}
                  />
                  <Segmented
                    label="Shelf life in"
                    value={shelfUnit}
                    onChange={(unit) => onShelf(shelfLife, unit)}
                    options={[
                      { value: 'day', label: 'Days' },
                      { value: 'month', label: 'Months' },
                      { value: 'year', label: 'Years' },
                    ]}
                    className={cx(
                      'self-center',
                      (!trackBatches || !trackExpiry) && 'pointer-events-none opacity-50',
                    )}
                  />
                </div>
              )}
            </Field>
          </div>
        )}
        {trackStock && (
          <Field
            label="Low-stock alert at"
            hint="Owners get a daily email when stock is at or below this."
          >
            {(id) => (
              <QtyInput
                id={id}
                milli={minStock}
                allowsDecimal={allowsDecimal}
                onValue={setMinStock}
              />
            )}
          </Field>
        )}
        {trackStock && groups.has('shelf') && (
          <Field label="Reorder quantity" hint="How much you usually order.">
            {(id) => (
              <QtyInput
                id={id}
                milli={reorderQty}
                allowsDecimal={allowsDecimal}
                onValue={setReorderQty}
              />
            )}
          </Field>
        )}
        {trackStock && !product && !hasVariants && trackSerials && (
          <Field
            label="Opening serial numbers"
            hint="One per line; the opening stock is their count."
            className="sm:col-span-2"
          >
            {(id) => (
              <div className="relative">
                <Textarea
                  id={id}
                  value={openingSerials}
                  onChange={(e) => setOpeningSerials(e.target.value)}
                  className="pr-11"
                />
                <ScanSerialsButton
                  text={openingSerials}
                  onText={setOpeningSerials}
                  label="Scan serials with the camera"
                  className="absolute right-1 top-1"
                />
              </div>
            )}
          </Field>
        )}
        {trackStock && !product && !hasVariants && !trackSerials && (
          <Field label="Opening stock">
            {(id) => (
              <QtyInput
                id={id}
                milli={opening}
                allowsDecimal={allowsDecimal}
                onValue={setOpening}
                placeholder="0"
              />
            )}
          </Field>
        )}
        {trackStock && !product && !hasVariants && !trackSerials && trackBatches && (
          <div className="grid animate-fade-in gap-3 rounded-xl border border-dashed border-primary/30 p-3 sm:col-span-2 sm:grid-cols-3">
            <Field label="Opening batch no." hint="Leave empty for stock without a batch.">
              {(id) => (
                <Input
                  id={id}
                  value={openingBatch.no}
                  onChange={(e) => setOpeningBatch((b) => ({ ...b, no: e.target.value }))}
                />
              )}
            </Field>
            <Field label="Mfg date">
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={openingBatch.mfg}
                  onChange={(e) => onOpeningMfg(e.target.value)}
                />
              )}
            </Field>
            {trackExpiry && (
              <Field
                label="Expiry date"
                hint={
                  shelfLifeDays ? (
                    <ExpiryHint
                      box={openingBatch}
                      mfg={openingBatch.mfg}
                      shelf={lifeRule}
                      onUse={(expiry) => setOpeningBatch((b) => ({ ...b, expiry, auto: true }))}
                    />
                  ) : undefined
                }
              >
                {(id) => (
                  <Input
                    id={id}
                    type="date"
                    value={openingBatch.expiry}
                    onChange={(e) =>
                      setOpeningBatch((b) => ({ ...b, expiry: e.target.value, auto: false }))
                    }
                  />
                )}
              </Field>
            )}
          </div>
        )}
        {trackStock && !product && lookups.activeLocations.length > 1 && (
          <Field label="Opening stock location">
            {(id) => (
              <LocationSelect
                id={id}
                locations={lookups.activeLocations}
                value={openingLocation ?? lookups.activeLocations[0]?.id ?? null}
                onChange={setOpeningLocation}
              />
            )}
          </Field>
        )}
        {canHaveVariants && (
          <>
            <SectionTitle>Variants</SectionTitle>
            <Checkbox
              className="sm:col-span-2"
              label="This product comes in variants (size, colour…)"
              checked={hasVariants}
              disabled={product?.kind === 'group'}
              onChange={(e) => setHasVariants(e.target.checked)}
            />
            {hasVariants && (
              <VariantsEditor
                options={optionRows}
                onOptions={setOptionRows}
                drafts={variantDrafts(draftOptions(optionRows), variants, variantEdits, sku)}
                onEdit={(key, edit) =>
                  setVariantEdits((all) => ({ ...all, [key]: { ...all[key], ...edit } }))
                }
                groupPrice={price}
                trackStock={trackStock && !trackSerials && !product}
                allowsDecimal={allowsDecimal}
                settings={settings}
              />
            )}
          </>
        )}
        {groups.has('packs') && !hasVariants && (
          <>
            <SectionTitle>Pack sizes & extra barcodes</SectionTitle>
            <PacksEditor
              packs={packs}
              onChange={setPacks}
              allowsDecimal={allowsDecimal}
              unitSymbol={creatingUnit ? newUnitSymbol : (unit?.symbol ?? '')}
            />
          </>
        )}
        <AttributeFields
          sections={attributeSections}
          values={attrs}
          onChange={(key, value) => setAttrs((a) => ({ ...a, [key]: value }))}
        />
        <Field label="Description (optional)" className="sm:col-span-2">
          {(id) => (
            <Textarea
              id={id}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
      </div>
    </Modal>
  )
}

/** The number cards' columns on a wide screen, by how many there are. */
const STAT_COLUMNS: Record<number, string> = {
  1: 'lg:grid-cols-2',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
}

export function ProductDetailScreen() {
  const { id = '' } = useParams()
  const repo = useRepo()
  const settings = useSettings()
  const lookups = useLookups()
  const navigate = useNavigate()
  const canEdit = useCan('staff')
  const canDelete = useCan('manager')
  const toast = useToast()
  const purchaseControl = useFeature('purchase_control')
  const [requesting, setRequesting] = useState(false)
  const [editing, setEditing] = useState(false)
  const [adjusting, setAdjusting] = useState(false)
  const [splitting, setSplitting] = useState<SplitSource | null | false>(false)
  const [datingBatch, setDatingBatch] = useState<RowOf<'batches'> | null>(null)
  const [deleting, setDeleting] = useState(false)
  const product = useLive((r) => r.get('products', id), [id], ['products'])
  const levels =
    useLive((r) => r.stock.levels({ productId: id }), [id], ['stock_movements']).data ?? []
  const codes =
    useLive(
      async (r) => (await r.list('product_codes', { where: { product_id: id }, limit: 200 })).rows,
      [id],
      ['product_codes'],
    ).data ?? []
  const batchRows =
    useLive(
      async (r) => (await r.list('batches', { where: { product_id: id }, limit: 1000 })).rows,
      [id],
      ['batches'],
    ).data ?? []
  const batchLevels =
    useLive((r) => r.stock.batchLevels({ productId: id }), [id], ['stock_movements']).data ?? []
  const [adjustPreset, setAdjustPreset] = useState<AdjustPreset | null>(null)
  const { custom } = useFieldGroups()
  const isGroup = product.data?.kind === 'group'
  const variants =
    useLive(
      async (r) =>
        isGroup
          ? (
              await r.list('products', {
                where: { parent_id: id },
                orderBy: { column: 'created_at' },
                limit: 1000,
              })
            ).rows
          : [],
      [id, isGroup],
      ['products'],
    ).data ?? []
  const stockTotals = useStockTotals()
  const serialCount =
    useLive(
      async (r) => (await r.list('product_serials', { where: { product_id: id }, limit: 1 })).total,
      [id],
      ['product_serials'],
    ).data ?? 0
  const parentId = product.data?.parent_id ?? null
  const parent = useLive(
    async (r) => (parentId ? r.get('products', parentId) : null),
    [parentId],
    ['products'],
  ).data

  const photoUrls = usePhotoUrls([product.data?.image_path])
  // Opened from Stock (?from=stock): the stock view of the page, back to Stock.
  const [params] = useSearchParams()
  const fromStock = params.get('from') === 'stock'
  const [receiving, setReceiving] = useState(false)
  const onOrder =
    useLive(
      async (r) => (await r.stock.onOrder())[id] ?? 0,
      [id],
      ['purchase_orders', 'purchase_order_lines', 'goods_receipt_lines'],
    ).data ?? 0
  const committed =
    useLive(
      async (r) => (await r.stock.committed())[id] ?? 0,
      [id],
      ['sales_orders', 'sales_order_lines', 'sales_invoices', 'sales_invoice_lines'],
    ).data ?? 0
  const sold30 = useLive(
    async (r) =>
      soldSince(
        (await r.stock.movements(id, { types: ['sale', 'sale_return'], limit: 5000 })).rows,
        daysAgo(new Date(), 30),
      ),
    [id],
    ['stock_movements'],
  ).data

  if (!product.data)
    return product.loading ? <SkeletonDetail /> : <EmptyState title="Product not found" />
  const p = product.data
  const photoUrl = photoUrls[p.image_path ?? '']
  const total = levels.reduce((a, l) => a + l.qty_milli, 0)
  const unit = lookups.unit(p.unit_id)
  const amount = (milli: number) => `${qty(milli)}${unit?.symbol ? ` ${unit.symbol}` : ''}`
  const tracked = p.track_stock && !isGroup
  const receivable = canEdit && tracked && total <= p.min_stock_milli
  const next = p.track_batches ? nextExpiries(batchRows, batchLevels).get(p.id) : undefined
  const nextBatch = next && batchRows.find((b) => b.batch_no === next.batchNo)
  const nextLeft = nextBatch
    ? batchLevels.filter((l) => l.batch_id === nextBatch.id).reduce((a, l) => a + l.qty_milli, 0)
    : 0
  const nextTone = next
    ? expiryBadge(next.expiry, todayIso(), settings?.expiry_alert_days ?? 30).tone
    : null
  const badge = (
    <StockBadge
      qtyMilli={total}
      minMilli={p.min_stock_milli}
      track={p.track_stock}
      service={settings?.business_type === 'workshop'}
    />
  )

  const priceCard = p.for_sale ? (
    <StatCard
      key="price"
      label="Selling price"
      value={money(p.sell_price_paisa, settings)}
      line={[
        lookups.taxRate(p.tax_rate_id)?.name ?? 'No VAT',
        p.tax_rate_id && p.price_includes_tax && 'VAT included',
        p.cost_price_paisa !== null && `cost ${money(p.cost_price_paisa, settings)}`,
        p.mrp_paisa !== null && `MRP ${money(p.mrp_paisa, settings)}`,
      ]
        .filter(Boolean)
        .join(' · ')}
    />
  ) : (
    <StatCard
      key="price"
      label="Cost price"
      value={p.cost_price_paisa !== null ? money(p.cost_price_paisa, settings) : '—'}
      line="An ingredient: not sold to customers."
    />
  )
  const stockCard = (
    <StatCard
      key="stock"
      label="In stock"
      value={p.track_stock ? amount(total) : '—'}
      tone={p.track_stock && total <= 0 ? 'danger' : undefined}
      line={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {badge}
          {p.min_stock_milli > 0 && <span>alert at {qty(p.min_stock_milli)}</span>}
          {fromStock && p.reorder_qty_milli ? (
            <span>· reorder {amount(p.reorder_qty_milli)}</span>
          ) : null}
        </span>
      }
    />
  )
  const soldCard = (
    <StatCard
      key="sold"
      label="Sold in 30 days"
      value={sold30 === undefined ? '…' : amount(sold30)}
      line={
        !sold30
          ? 'No sales in 30 days'
          : sold30 >= 30_000
            ? `about ${qty(Math.round(sold30 / 30_000) * 1000)} a day`
            : `about ${qty(Math.max(1000, Math.round((sold30 * 7) / 30_000) * 1000))} a week`
      }
    />
  )
  const onOrderCard = (
    <StatCard
      key="order"
      label="On order"
      value={amount(onOrder)}
      line={
        onOrder > 0 ? (
          <Link to="/purchase-orders" className="font-semibold text-primary hover:underline">
            On sent purchase orders
          </Link>
        ) : (
          'Nothing on order'
        )
      }
    />
  )
  const expiryCard = next ? (
    <StatCard
      key="expiry"
      label="Next to expire"
      value={next.expiry}
      tone={nextTone === 'danger' ? 'danger' : nextTone === 'warning' ? 'warning' : undefined}
      line={`${next.batchNo} · ${amount(nextLeft)}`}
    />
  ) : null
  const freeCard =
    committed > 0 ? (
      <StatCard
        key="free"
        label="Free to sell"
        value={amount(total - committed)}
        tone={total - committed < 0 ? 'danger' : undefined}
        line={`${amount(committed)} ordered by customers`}
      />
    ) : null
  const valueCard = (
    <StatCard
      key="value"
      label="Stock value"
      value={
        p.cost_price_paisa !== null && total > 0
          ? money(mulDivRound(total, p.cost_price_paisa, 1000), settings)
          : '—'
      }
      line="At cost price"
    />
  )
  const cards = isGroup
    ? [priceCard]
    : !p.track_stock
      ? [priceCard, soldCard]
      : fromStock
        ? [stockCard, freeCard ?? soldCard, onOrderCard, expiryCard ?? valueCard]
        : [
            priceCard,
            stockCard,
            onOrder > 0 ? onOrderCard : soldCard,
            ...(expiryCard ? [expiryCard] : onOrder > 0 ? [soldCard] : []),
          ]

  return (
    <div>
      <Link
        to={
          fromStock ? '/stock' : settings?.restaurant && !p.for_sale ? '/ingredients' : '/products'
        }
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" />{' '}
        {fromStock
          ? 'Stock'
          : !settings?.restaurant
            ? 'Products'
            : p.for_sale
              ? 'Menu'
              : 'Ingredients'}
      </Link>
      {parent && (
        <Link
          to={`/products/${parent.id}`}
          className="mb-3 ml-4 inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          Variant of {parent.name}
        </Link>
      )}
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            {photoUrl ? (
              <ProductPhoto
                url={photoUrl}
                alt={`Photo of ${p.name}`}
                className="size-14 shrink-0 rounded-xl border border-border"
              />
            ) : (
              <IconTile area="stock" size="lg">
                <Package className="size-5" />
              </IconTile>
            )}
            <span className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
              {p.name}
              {/* Untracked: "Service" (workshops) or "Not tracked", as in the list. */}
              {!isGroup && <span className="text-base font-normal">{badge}</span>}
            </span>
          </span>
        }
        subtitle={[p.sku, p.barcode, lookups.brand(p.brand_id)?.name].filter(Boolean).join(' · ')}
        actions={
          <>
            {receivable && (
              <Button
                variant="soft"
                icon={<Package className="size-4" />}
                onClick={() => setReceiving(true)}
              >
                Receive
              </Button>
            )}
            {purchaseControl && canEdit && tracked && (
              <Button
                variant="secondary"
                icon={<Hand className="size-4" />}
                onClick={() => setRequesting(true)}
              >
                Request more
              </Button>
            )}
            {canEdit && p.track_stock && (
              <Button
                variant="secondary"
                icon={<SlidersHorizontal className="size-4" />}
                onClick={() => setAdjusting(true)}
              >
                Adjust stock
              </Button>
            )}
            {canEdit && (
              <Button
                variant="secondary"
                icon={<Pencil className="size-4" />}
                onClick={() => setEditing(true)}
              >
                Edit
              </Button>
            )}
          </>
        }
        more={
          canDelete && (
            <Button
              variant="ghost"
              className="text-danger hover:text-danger"
              icon={<Trash2 className="size-4" />}
              onClick={() => setDeleting(true)}
            >
              Delete
            </Button>
          )
        }
      />
      <div className={cx('grid grid-cols-2 gap-3', STAT_COLUMNS[cards.length])}>{cards}</div>
      {isGroup && (
        <VariantsCard
          group={p}
          variants={variants}
          stock={stockTotals}
          unitSymbol={unit?.symbol ?? ''}
          settings={settings}
        />
      )}
      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-start">
        {/* Batches and serials bring their own top margin: the gap spaces them here. */}
        <div className="flex min-w-0 flex-col gap-4 *:mt-0">
          {(p.track_batches || batchRows.length > 0) && (
            <BatchesCard
              batches={sortedBatches(batchRows)}
              levels={batchLevels}
              lookups={lookups}
              unitSymbol={unit?.symbol ?? ''}
              alertDays={settings?.expiry_alert_days ?? 30}
              settings={settings}
              canWriteOff={canEdit}
              noBatch={noBatchLevels(levels, batchLevels)}
              onEditDates={canEdit ? setDatingBatch : undefined}
              onSplit={
                canEdit && p.track_batches && !p.track_serials && canSplit(levels, batchLevels)
                  ? (source) => setSplitting(source ?? null)
                  : undefined
              }
              onWriteOff={(b, locationId, qtyMilli) => {
                setAdjustPreset({
                  mode: 'remove',
                  batchId: b.id,
                  locationId,
                  amount: qtyMilli,
                  note: 'Expired',
                })
                setAdjusting(true)
              }}
            />
          )}
          {(p.track_serials || serialCount > 0) && <SerialsCard product={p} />}
          <StockHistory
            productId={p.id}
            batches={p.track_batches || batchRows.length > 0 ? batchRows : null}
            lookups={lookups}
            settings={settings}
          />
        </div>
        <div className="flex flex-col gap-4">
          {!isGroup && (
            <Card className="p-0">
              <h2 className="border-b border-border px-5 py-3 font-semibold">Where it is</h2>
              {levels.length === 0 ? (
                <p className="px-5 py-3 text-sm text-muted">No stock recorded.</p>
              ) : (
                <ul className="divide-y divide-border text-sm">
                  {levels.map((l) => (
                    <li key={l.location_id} className="flex justify-between px-5 py-2">
                      <span>{lookups.location(l.location_id)?.name ?? 'Unknown'}</span>
                      <span className="tabular font-semibold">{amount(l.qty_milli)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
          <ProductDetailsCard
            product={p}
            lookups={lookups}
            codes={codes}
            custom={custom}
            unitSymbol={unit?.symbol ?? ''}
            settings={settings}
            side
            showPrice={fromStock}
          />
          {p.supplier_id && <UsualSupplier id={p.supplier_id} />}
        </div>
      </div>

      {settings?.restaurant && !isGroup && p.for_sale && <RecipeCard product={p} />}

      {receiving && <ReceiveStockDialog products={[p]} onClose={() => setReceiving(false)} />}
      {requesting && (
        <RequestMoreDialog product={p} stockMilli={total} onClose={() => setRequesting(false)} />
      )}
      {editing && (
        <ProductForm
          product={p}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}
      {datingBatch && (
        <EditBatchDialog product={p} batch={datingBatch} onClose={() => setDatingBatch(null)} />
      )}
      {splitting !== false && (
        <SplitBatchesDialog
          product={p}
          batches={sortedBatches(batchRows)}
          levels={levels}
          batchLevels={batchLevels}
          lookups={lookups}
          unitSymbol={unit?.symbol ?? ''}
          allowsDecimal={unit?.allows_decimal ?? false}
          start={splitting}
          onClose={() => setSplitting(false)}
        />
      )}
      {adjusting && (
        <AdjustStockDialog
          productId={p.id}
          allowsDecimal={unit?.allows_decimal ?? false}
          levels={levels}
          batches={p.track_batches ? sortedBatches(batchRows) : undefined}
          batchLevels={batchLevels}
          shelf={{ amount: p.shelf_life_days, unit: p.shelf_life_unit }}
          preset={adjustPreset}
          onClose={() => {
            setAdjusting(false)
            setAdjustPreset(null)
          }}
        />
      )}
      <ConfirmDialog
        open={deleting}
        title="Delete this product?"
        message="It disappears from the catalogue. Past invoices and stock history keep it."
        confirmLabel="Delete"
        danger
        onClose={() => setDeleting(false)}
        onConfirm={() =>
          void repo
            .commit([
              ...variants.map((v) => deleteOp('products', v.id)),
              deleteOp('products', p.id),
            ])
            .then(
              () => navigate('/products'),
              (e: unknown) => toast.error(errorMessage(e)),
            )
        }
      />
    </div>
  )
}

interface AdjustPreset {
  mode: 'count' | 'add' | 'remove'
  batchId: string
  locationId: string
  amount: number
  note: string
}

function AdjustStockDialog({
  productId,
  allowsDecimal,
  levels,
  batches,
  batchLevels,
  shelf = { amount: null, unit: null },
  preset,
  onClose,
}: {
  productId: string
  allowsDecimal: boolean
  levels: { location_id: string; qty_milli: number }[]
  /** Set for batch-tracked products: the stock change names a batch. */
  batches?: RowOf<'batches'>[]
  batchLevels?: BatchLevel[]
  /** The product's shelf life, for a new batch's expiry. */
  shelf?: { amount: number | null; unit: ShelfLifeUnit | null }
  preset?: AdjustPreset | null
  onClose: () => void
}) {
  const repo = useRepo()
  const lookups = useLookups()
  const { current } = useWorkspace()
  const toast = useToast()
  const [mode, setMode] = useState<'count' | 'add' | 'remove'>(preset?.mode ?? 'count')
  const [locationId, setLocationId] = useState<string | null>(preset?.locationId ?? null)
  const [batchId, setBatchId] = useState<string>(preset?.batchId ?? batches?.[0]?.id ?? '')
  const [amount, setAmount] = useState<number | null>(preset?.amount ?? null)
  const [note, setNote] = useState(preset?.note ?? '')
  const [newBatch, setNewBatch] = useState({ no: '', mfg: '', expiry: '', auto: false })
  const [busy, setBusy] = useState(false)
  const location = locationId ?? lookups.activeLocations[0]?.id ?? null
  const byBatch = batches !== undefined
  const isNew = batchId === NEW_BATCH
  // Stock with no batch is never in batch_stock: it is what the batches leave of the place's stock.
  const loose = noBatchLevels(
    levels.filter((l) => l.location_id === location),
    (batchLevels ?? []).filter((l) => l.location_id === location),
  )
  const batchQty = (id: string) =>
    id === ''
      ? (loose.get(location ?? '') ?? 0)
      : (batchLevels ?? [])
          .filter((l) => l.location_id === location && l.batch_id === id)
          .reduce((a, l) => a + l.qty_milli, 0)
  const currentQty = byBatch
    ? isNew
      ? 0
      : batchQty(batchId)
    : (levels.find((l) => l.location_id === location)?.qty_milli ?? 0)
  const delta =
    amount === null ? 0 : mode === 'count' ? amount - currentQty : mode === 'add' ? amount : -amount

  const save = async () => {
    if (!current || !location || delta === 0) return onClose()
    if (isNew && !newBatch.no.trim()) {
      toast.error('Enter the new batch’s number.')
      return
    }
    setBusy(true)
    try {
      const ctx = { tenantId: current.tenant_id }
      // A new batch is saved with the change; a number that exists is that batch.
      const made = isNew
        ? batchOp(
            ctx,
            productId,
            {
              batchNo: newBatch.no,
              mfgDate: newBatch.mfg || null,
              expiryDate: newBatch.expiry || null,
            },
            (batches ?? []).find(
              (b) => b.batch_no.toLowerCase() === newBatch.no.trim().toLowerCase(),
            ) ?? null,
          )
        : null
      await repo.commit([
        ...(made?.op ? [made.op] : []),
        insertOp(
          'stock_movements',
          newMovement(
            { tenantId: current.tenant_id },
            {
              product_id: productId,
              location_id: location,
              type: mode === 'count' ? 'count' : 'adjustment',
              qty_milli: delta,
              ref_type: mode === 'count' ? 'count' : 'manual',
              notes: note.trim() || null,
              batch_id: made ? made.id : byBatch && batchId ? batchId : null,
            },
          ),
        ),
      ])
      toast.success('Stock updated')
      onClose()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Adjust stock"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={amount === null} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-1 rounded-xl bg-soft p-1 text-sm">
          {(
            [
              ['count', 'Set count'],
              ['add', 'Add'],
              ['remove', 'Remove'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setMode(value)}
              className={`rounded-lg py-1.5 font-medium ${mode === value ? 'bg-surface shadow-card' : 'text-muted'}`}
            >
              {label}
            </button>
          ))}
        </div>
        {lookups.activeLocations.length > 1 && (
          <Field label="Location">
            {(id) => (
              <LocationSelect
                id={id}
                locations={lookups.activeLocations}
                value={location}
                onChange={setLocationId}
              />
            )}
          </Field>
        )}
        {byBatch && (
          <Field label="Batch">
            {(id) => (
              <Select id={id} value={batchId} onChange={(e) => setBatchId(e.target.value)}>
                <option value="">No batch · {qty(batchQty(''))} here</option>
                {(batches ?? []).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.batch_no}
                    {b.expiry_date ? ` · exp ${b.expiry_date}` : ''} · {qty(batchQty(b.id))} here
                  </option>
                ))}
                <option value={NEW_BATCH}>+ New batch…</option>
              </Select>
            )}
          </Field>
        )}
        {isNew && (
          <div className="grid animate-fade-in grid-cols-2 gap-2 rounded-xl border border-dashed border-primary/30 p-3">
            <Field label="Batch no." className="col-span-2">
              {(id) => (
                <Input
                  id={id}
                  value={newBatch.no}
                  onChange={(e) => setNewBatch((b) => ({ ...b, no: e.target.value }))}
                />
              )}
            </Field>
            <Field label="Mfg date">
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={newBatch.mfg}
                  onChange={(e) =>
                    setNewBatch((b) => ({
                      ...b,
                      mfg: e.target.value,
                      ...followExpiry(e.target.value, b, shelf),
                    }))
                  }
                />
              )}
            </Field>
            <Field label="Expiry date">
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={newBatch.expiry}
                  onChange={(e) =>
                    setNewBatch((b) => ({ ...b, expiry: e.target.value, auto: false }))
                  }
                />
              )}
            </Field>
          </div>
        )}
        <Field
          label={mode === 'count' ? 'Counted quantity' : 'Quantity'}
          hint={`Currently ${qty(currentQty)} here. Change: ${delta > 0 ? '+' : ''}${qty(delta)}`}
        >
          {(id) => (
            <QtyInput
              id={id}
              milli={amount}
              allowsDecimal={allowsDecimal}
              onValue={setAmount}
              autoFocus={finePointer}
            />
          )}
        </Field>
        <Field label="Note (optional)">
          {(id) => (
            <Input
              id={id}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. damaged, found in store room"
            />
          )}
        </Field>
      </div>
    </Modal>
  )
}

/** The batch list's last choice: a batch made with the change. */
const NEW_BATCH = 'new'

function sortedBatches(rows: RowOf<'batches'>[]): RowOf<'batches'>[] {
  return [...rows].sort(
    (a, b) =>
      (a.expiry_date ?? '9999').localeCompare(b.expiry_date ?? '9999') ||
      a.batch_no.localeCompare(b.batch_no),
  )
}

function BatchesCard({
  batches,
  levels,
  lookups,
  unitSymbol,
  alertDays,
  settings,
  canWriteOff,
  onWriteOff,
  noBatch,
  onSplit,
  onEditDates,
}: {
  batches: RowOf<'batches'>[]
  levels: BatchLevel[]
  lookups: ReturnType<typeof useLookups>
  unitSymbol: string
  alertDays: number
  settings: ReturnType<typeof useSettings>
  canWriteOff: boolean
  onWriteOff: (batch: RowOf<'batches'>, locationId: string, qtyMilli: number) => void
  /** Stock with no batch, by place (`noBatchLevels`). */
  noBatch: Map<string, number>
  /** Set when stock can be split into batches here. */
  onSplit?: (source?: SplitSource) => void
  /** Set when a batch's dates may be changed here. */
  onEditDates?: (batch: RowOf<'batches'>) => void
}) {
  const today = todayIso()
  const loose = [...noBatch].filter(([, q]) => q > 0)
  const looseTotal = loose.reduce((a, [, q]) => a + q, 0)
  const splitButton = (source: SplitSource, name: string) =>
    onSplit && (
      <Button
        size="sm"
        variant="secondary"
        aria-label={`Split ${name}`}
        onClick={() => onSplit(source)}
      >
        <ArrowsSplit className="size-4" />
        Split
      </Button>
    )
  return (
    <Card className="mt-4 p-0">
      <div className="flex items-center gap-3 border-b border-border py-2.5 pl-5 pr-3">
        <h2 className="font-semibold">Batches</h2>
        {onSplit && (
          <Button size="sm" variant="secondary" className="ml-auto" onClick={() => onSplit()}>
            <ArrowsSplit className="size-4" />
            Split into batches
          </Button>
        )}
      </div>
      {batches.length === 0 && looseTotal <= 0 ? (
        <p className="p-5 text-sm text-muted">
          No batches yet. Receive stock with a batch number to start.
        </p>
      ) : (
        <Table stack>
          <thead>
            <tr>
              <TH>Batch</TH>
              <TH>Mfg</TH>
              <TH>Expiry</TH>
              <TH>Where</TH>
              <TH align="right">In stock</TH>
              <TH />
            </tr>
          </thead>
          <tbody>
            {looseTotal > 0 && (
              <tr>
                <TD className="stack-main stack-r1">
                  <span className="font-medium text-muted">No batch</span>
                  <div className="text-xs text-muted">
                    Stock from before batches, or received without a number
                  </div>
                </TD>
                <TD className="max-sm:hidden">—</TD>
                <TD className="max-sm:hidden">—</TD>
                <TD className="stack-r4 text-xs text-muted">
                  {loose.map(([l]) => lookups.location(l)?.name ?? '').join(', ')}
                </TD>
                <TD align="right" className="stack-end stack-r1 font-semibold sm:font-normal">
                  {qty(looseTotal)} {unitSymbol}
                </TD>
                <TD align="right" className="max-sm:mt-2 max-sm:text-left">
                  {loose[0] && splitButton({ batchId: null, locationId: loose[0][0] }, 'No batch')}
                </TD>
              </tr>
            )}
            {batches.map((b) => {
              const here = levels.filter((l) => l.batch_id === b.id && l.qty_milli !== 0)
              const total = here.reduce((a, l) => a + l.qty_milli, 0)
              const badge = expiryBadge(b.expiry_date, today, alertDays)
              return (
                <tr key={b.id} className={total <= 0 ? 'text-muted' : undefined}>
                  <TD className="stack-main stack-r1 font-medium">
                    {b.batch_no}
                    {b.mrp_paisa !== null && (
                      <div className="text-xs text-muted">MRP {money(b.mrp_paisa, settings)}</div>
                    )}
                  </TD>
                  <TD className="stack-r2 max-sm:text-xs max-sm:text-muted max-sm:before:content-['Mfg_']">
                    {b.mfg_date ?? '—'}
                  </TD>
                  <TD className="stack-r3 max-sm:text-xs max-sm:before:content-['Exp_']">
                    {b.expiry_date ?? '—'}
                    {b.expiry_date && total > 0 && (
                      <Badge tone={badge.tone} className="ml-2">
                        {badge.label}
                      </Badge>
                    )}
                  </TD>
                  <TD className="stack-r4 text-xs text-muted">
                    {here.map((l) => lookups.location(l.location_id)?.name ?? '').join(', ') || '—'}
                  </TD>
                  <TD align="right" className="stack-end stack-r1 font-semibold sm:font-normal">
                    {qty(total)} {unitSymbol}
                  </TD>
                  <TD align="right" className="whitespace-nowrap max-sm:mt-2 max-sm:text-left">
                    {onEditDates && (
                      <IconButton
                        label={`Change the dates of ${b.batch_no}`}
                        onClick={() => onEditDates(b)}
                        className="mr-1"
                      >
                        <Pencil className="size-4" />
                      </IconButton>
                    )}
                    {badge.status !== 'expired' &&
                      total > 0 &&
                      here[0] &&
                      splitButton({ batchId: b.id, locationId: here[0].location_id }, b.batch_no)}
                    {canWriteOff && badge.status === 'expired' && total > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const l = here.find((x) => x.qty_milli > 0)
                          if (l) onWriteOff(b, l.location_id, l.qty_milli)
                        }}
                      >
                        Write off
                      </Button>
                    )}
                  </TD>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}
    </Card>
  )
}

/** The product page's card for who it is usually bought from. */
function UsualSupplier({ id }: { id: string }) {
  const supplier = useLive((r) => r.get('suppliers', id), [id], ['suppliers']).data
  if (!supplier) return null
  return (
    <Card className="p-0">
      <h2 className="border-b border-border px-5 py-3 font-semibold">Usual supplier</h2>
      <div className="flex items-center gap-3 px-5 py-3">
        <IconTile area="buy" size="sm">
          <Truck className="size-4" />
        </IconTile>
        <Link to={`/suppliers/${id}`} className="font-semibold text-primary hover:underline">
          {supplier.name}
        </Link>
      </div>
    </Card>
  )
}
